/**
 * Synchronously resolve the variant a quick-add card currently represents,
 * from the escaped variants JSON that every product-card snippet renders as
 * `data-variants`. No network, no events, no side effects.
 *
 * The ATC button's `data-variant-id` is written asynchronously (ProductOptions
 * has to fetch /products/{handle}.js before it can dispatch `variant:change`),
 * so it goes stale the instant a shopper changes a swatch. Anything that needs
 * a trustworthy variant id -- above all the add-to-cart POST -- must resolve
 * from the live DOM instead of reading that attribute.
 *
 * @param {HTMLElement} el  the <quick-add> / [js-product-quickadd] element
 * @param {{strict?: boolean}} [opts]
 *        strict (default true): every option group this card renders as radios
 *        must have a checked value, otherwise return null.
 * @returns {object|null} the matching variant object, or null
 */
function resolveQuickAddVariant(el, opts) {
  if (!el) return null;

  const strict = !opts || opts.strict !== false;
  const KEYS = ['option1', 'option2', 'option3'];

  let variants;
  try {
    variants = JSON.parse(el.dataset.variants || 'null');
  } catch (e) {
    return null;
  }
  if (!Array.isArray(variants) || !variants.length) return null;

  const inputs = [...el.querySelectorAll('[js-product-swatch-input]')];
  if (!inputs.length) return null; // single-variant card: caller falls back

  // Option groups this card actually renders as radios. product-option-2 only
  // emits inputs for Size/Style/Length/Denominations, so a Color option has no
  // group here -- completeness has to be measured against the DOM, not against
  // the number of options the product has.
  const groups = new Set();
  const optionMap = {};
  inputs.forEach((input) => {
    const index = input.dataset.index;
    if (!index) return;
    groups.add(index);
    if (input.checked) optionMap[index] = input.value;
  });

  if (strict) {
    for (const group of groups) {
      if (typeof optionMap[group] !== 'string') return null; // incomplete selection
    }
  }

  // Same normalisation ProductOptions._getVariantFromOptions uses, because the
  // liquid strips quotes out of the swatch values.
  const norm = (v) => String(v == null ? '' : v).trim().replace(/"/g, '');
  const matches = (map) =>
    variants.find((v) =>
      KEYS.every((k) => map[k] == null || norm(v[k]) === norm(map[k]))
    ) || null;

  // Pin the axes this card does NOT render (e.g. Color) to whatever the button
  // already points at, so a size-only match can't jump to another colour.
  const button = el.querySelector('[js-add-to-cart]');
  const pinned =
    button && button.dataset.variantId
      ? variants.find((v) => String(v.id) === String(button.dataset.variantId))
      : null;

  if (pinned) {
    const pinnedMap = Object.assign({}, optionMap);
    KEYS.forEach((k) => {
      if (!groups.has(k) && pinned[k] != null && pinnedMap[k] == null) {
        pinnedMap[k] = pinned[k];
      }
    });
    // Fall through to the unpinned match if the pin itself was stale/foreign;
    // the shopper's own selections are never relaxed.
    const withPin = matches(pinnedMap);
    if (withPin) return withPin;
  }

  return matches(optionMap);
}

function variantChecker() {
  document.querySelectorAll('[js-product-quickadd]').forEach(productCard => {
    const swatchInputs = productCard.querySelectorAll('[js-product-swatch-input]');

    function runCheck(activeInput) {
      if (!activeInput) return;

      const currentIndex = activeInput.getAttribute('data-index');
      const availableValues = activeInput.dataset.availableValues?.split(',') || [];

      swatchInputs.forEach(otherInput => {
        const otherIndex = otherInput.getAttribute('data-index');

        // Skip same option group
        if (otherIndex === currentIndex) return;

        const otherValue = otherInput.value;
        const label = productCard.querySelector(`label[for="${otherInput.id}"]`);
        if (!label) return;

        if (availableValues.includes(otherValue)) {
          otherInput.disabled = false;

          label.classList.remove(
            'swatch__soldout',
            'text-gray-medium',
            'border-gray-medium',
            'no-matching'
          );

          label.classList.add(
            'text-gray-dark',
            'border-gray-dark',
            'availbledata',
            'matching'
          );
        } else {
          otherInput.disabled = false; // set true if you want hard disable

          label.classList.add(
            'swatch__soldout',
            'text-gray-medium',
            'border-gray-medium',
            'no-matching'
          );

          label.classList.remove(
            'text-gray-dark',
            'border-gray-dark',
            'availbledata',
            'matching'
          );
        }
      });
    }

    // 🔹 Ensure each option group has a default selection
    const optionGroups = {};
    swatchInputs.forEach(input => {
      const idx = input.getAttribute('data-index');
      if (!optionGroups[idx]) optionGroups[idx] = [];
      optionGroups[idx].push(input);
    });

    Object.values(optionGroups).forEach(inputs => {
      const hasChecked = inputs.some(i => i.checked);
      if (!hasChecked && inputs.length > 0) {
        // Select first non-soldout option, or first option as fallback
        const firstAvailable = inputs.find(i => {
          const label = productCard.querySelector(`label[for="${i.id}"]`);
          return label && !label.classList.contains('swatch__soldout');
        }) || inputs[0];
        firstAvailable.checked = true;
        const label = productCard.querySelector(`label[for="${firstAvailable.id}"]`);
        if (label) label.classList.add('swatch__selected');
      }
    });

    // 🔹 Sync data-variant-id on the ATC button to match the auto-selected options.
    // variantChecker sets .checked programmatically which does not fire a change event,
    // so ProductOptions never resolves the variant. Must run after the force-check loop
    // above and before anything else writes data-variant-id, so resolveQuickAddVariant's
    // colour pin still reads the pristine liquid default.
    const resolved = resolveQuickAddVariant(productCard);
    if (resolved) {
      const atcButton = productCard.querySelector('[js-add-to-cart]');
      if (atcButton) atcButton.dataset.variantId = String(resolved.id);
    }

    // 🔹 Run once on load
    const initiallyChecked =
      productCard.querySelector('[js-product-swatch-input]:checked') ||
      swatchInputs[0];

    runCheck(initiallyChecked);

    // 🔹 Run on swatch change (only bind once)
    swatchInputs.forEach(input => {
      if (!input._variantCheckerBound) {
        input._variantCheckerBound = true;
        input.addEventListener('change', function () {
          runCheck(this);
        });
      }
    });
  });
}

// Call after DOM is ready
document.addEventListener('DOMContentLoaded', variantChecker);


(window.theme = window.theme || {}),
  void 0 === window.Shopify && (window.Shopify = {}),
  (Shopify.bind = function (e, t) {
    return function () {
      return e.apply(t, arguments);
    };
  }),
  (Shopify.setSelectorByValue = function (e, t) {
    for (var s = 0, r = e.options.length; s < r; s++) {
      var i = e.options[s];
      if (t == i.value || t == i.innerHTML) return (e.selectedIndex = s);
    }
  }),
  (Shopify.addListener = function (e, t, s) {
    e.addEventListener
      ? e.addEventListener(t, s, !1)
      : e.attachEvent("on" + t, s);
  }),
  (Shopify.postLink = function (e, t) {
    var s,
      r = (t = t || {}).method || "post",
      i = t.parameters || {},
      o = document.createElement("form");
    for (s in (o.setAttribute("method", r), o.setAttribute("action", e), i)) {
      var n = document.createElement("input");
      n.setAttribute("type", "hidden"),
        n.setAttribute("name", s),
        n.setAttribute("value", i[s]),
        o.appendChild(n);
    }
    document.body.appendChild(o), o.submit(), document.body.removeChild(o);
  }),
  (Shopify.CountryProvinceSelector = function (e, t, s) {
    (this.countryEl = document.getElementById(e)),
      (this.provinceEl = document.getElementById(t)),
      (this.provinceContainer = document.getElementById(s.hideElement || t)),
      Shopify.addListener(
        this.countryEl,
        "change",
        Shopify.bind(this.countryHandler, this)
      ),
      this.initCountry(),
      this.initProvince();
  }),
  (Shopify.CountryProvinceSelector.prototype = {
    initCountry: function () {
      var e = this.countryEl.getAttribute("data-default");
      Shopify.setSelectorByValue(this.countryEl, e), this.countryHandler();
    },
    initProvince: function () {
      var e = this.provinceEl.getAttribute("data-default");
      e &&
        0 < this.provinceEl.options.length &&
        Shopify.setSelectorByValue(this.provinceEl, e);
    },
    countryHandler: function (e) {
      var t = (r =
        this.countryEl.options[this.countryEl.selectedIndex]).getAttribute(
          "data-provinces"
        ),
        s = JSON.parse(t);
      if ((this.clearOptions(this.provinceEl), s && 0 == s.length))
        this.provinceContainer.style.display = "none";
      else {
        for (var r, i = 0; i < s.length; i++)
          ((r = document.createElement("option")).value = s[i][0]),
            (r.innerHTML = s[i][1]),
            this.provinceEl.appendChild(r);
        this.provinceContainer.style.display = "";
      }
    },
    clearOptions: function (e) {
      for (; e.firstChild;) e.removeChild(e.firstChild);
    },
    setOptions: function (e, t) {
      var s = 0;
      for (t.length; s < t.length; s++) {
        var r = document.createElement("option");
        (r.value = t[s]), (r.innerHTML = t[s]), e.appendChild(r);
      }
    },
  }),
  (theme.config = {
    mediaQueries: {
      landscapeAll: "screen and (orientation: landscape)",
      landscape:
        "screen and (orientation: landscape), screen and (min-width: 48rem)",
      belowTablet: "screen and (max-width: 47.99rem)",
      tablet: "screen and (min-width: 48rem)",
      belowLaptop: "screen and (max-width: 63.99rem)",
      laptop: "screen and (min-width: 64rem)",
      belowDesktop: "screen and (max-width: 64rem)",
      desktop: "screen and (min-width: 64.01rem)",
      belowWidescreen: "screen and (max-width: 79.99rem)",
      widescreen: "screen and (min-width: 80rem)",
      belowExtrawide: "screen and (max-width: 89.99rem)",
      extrawide: "screen and (min-width: 90rem)",
    },
    money: {
      format: `${window.currency.symbol || "$"}{{amount}}`,
      currencySymbols: {
        USD: "$",
        CAD: "$",
        EUR: "€",
        CRC: "₡",
        GBP: "£",
        ILS: "₪",
        INR: "₹",
        JPY: "¥",
        KRW: "₩",
        NGN: "₦",
        PHP: "₱",
        PLN: "zł",
        PYG: "₲",
        THB: "฿",
        UAH: "₴",
        VND: "₫",
      },
    },
    upsellLimit: 6,
  }),
  (theme.utils = {
    a11y: {
      getFocusableElements(e) {
        return Array.from(
          e.querySelectorAll(
            "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
          )
        );
      },
      _trapFocusHandlers: {},
      trapFocus(t, e = t) {
        var s = theme.utils.a11y.getFocusableElements(t),
          r = s[0],
          i = s[s.length - 1];
        theme.utils.a11y.removeTrapFocus(),
          (theme.utils.a11y._trapFocusHandlers.focusin = (e) => {
            (e.target !== t && e.target !== i && e.target !== r) ||
              document.addEventListener(
                "keydown",
                theme.utils.a11y._trapFocusHandlers.keydown
              );
          }),
          (theme.utils.a11y._trapFocusHandlers.focusout = function () {
            document.removeEventListener(
              "keydown",
              theme.utils.a11y._trapFocusHandlers.keydown
            );
          }),
          (theme.utils.a11y._trapFocusHandlers.keydown = function (e) {
            "TAB" === e.code.toUpperCase() &&
              (e.target !== i || e.shiftKey || (e.preventDefault(), r.focus()),
                (e.target !== t && e.target !== r) ||
                !e.shiftKey ||
                (e.preventDefault(), i.focus()));
          }),
          document.addEventListener(
            "focusout",
            theme.utils.a11y._trapFocusHandlers.focusout
          ),
          document.addEventListener(
            "focusin",
            theme.utils.a11y._trapFocusHandlers.focusin
          ),
          e.focus();
      },
      removeTrapFocus(e = null) {
        document.removeEventListener(
          "focusin",
          theme.utils.a11y._trapFocusHandlers.focusin
        ),
          document.removeEventListener(
            "focusout",
            theme.utils.a11y._trapFocusHandlers.focusout
          ),
          document.removeEventListener(
            "keydown",
            theme.utils.a11y._trapFocusHandlers.keydown
          ),
          e && e.focus();
      },
    },
    prepareTransition(e) {
      e.addEventListener("transitionend", () => {
        e.classList.remove("is-transitioning"),
          e.classList.remove("is-transitioning-flex");
      });
      let t = 0;
      const s = getComputedStyle(e);
      [
        "transition-duration",
        "-moz-transition-duration",
        "-webkit-transition-duration",
        "-o-transition-duration",
      ].forEach((e) => (t = t || parseFloat(s.getPropertyValue(e)))),
        0 != t &&
        ("flex" == e.dataset.transition
          ? e.classList.add("is-transitioning-flex")
          : e.classList.add("is-transitioning"),
          e.offsetWidth);
    },
    defaultTo: function (e, t) {
      return null == e || e != e ? t : e;
    },
    formatMoney(e, t) {
      "string" == typeof e && (e = e.replace(".", ""));
      var s = "",
        r = /\{\{\s*(\w+)\s*\}\}/,
        t = t || theme.config.money.format;
      function i(e, t, s, r) {
        if (
          ((t = theme.utils.defaultTo(t, 2)),
            (s = theme.utils.defaultTo(s, ",")),
            (r = theme.utils.defaultTo(r, ".")),
            isNaN(e) || null == e)
        )
          return 0;
        t = (e = (e / 100).toFixed(t)).split(".");
        return (
          t[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + s) +
          (t[1] ? r + t[1] : "")
        );
      }
      switch (t.match(r)[1]) {
        case "amount":
          s = i(e, 2);
          break;
        case "amount_no_decimals":
          s = i(e, 0);
          break;
        case "amount_with_space_separator":
          s = i(e, 2, " ", ".");
          break;
        case "amount_no_decimals_with_comma_separator":
          s = i(e, 0, ",", ".");
          break;
        case "amount_no_decimals_with_space_separator":
          s = i(e, 0, " ");
      }
      return t.replace(r, s).replace(/\.00$/, '');
    },
    serializeForm: (e) => {
      const t = {};
      const s = new FormData(e);

      for (const r of s.keys()) {
        const i = /(?:^(properties\[))(.*?)(?:\]$)/;
        if (i.test(r)) {
          t.properties = t.properties || {};
          t.properties[i.exec(r)[2]] = s.get(r);
        } else {
          t[r] = s.get(r);
        }
      }

      // Set the "id" field to the value of "product-selected-id"
      if (t["product-selected-id"]) {
        t["id"] = t["product-selected-id"];
      }

      return JSON.stringify(t);
    },
    debounce(t, s) {
      let r;
      return (...e) => {
        clearTimeout(r), (r = setTimeout(() => t.apply(this, e), s));
      };
    },
  });
class CartAPI {
  static async get() {
    CartAPI._trigger(CartAPI._events.fetch.before);
    try {
      let e = await fetch("/cart.js");
      var t = await e.json();
      return CartAPI._trigger(CartAPI._events.fetch.complete, { cart: t }), t;
    } catch (e) {
      CartAPI._trigger(CartAPI._events.fetch.error, { error: e }),
        CartAPI._onError(e);
    }
  }
  static async add(t) {
    CartAPI._trigger(CartAPI._events.add.before);
    try {
      let e = await fetch("/cart/add.js", {
        method: "POST",
        body: JSON.stringify(t),
        ...CartAPI._fetchConfig,
      });
      e = await CartAPI._parse(e);
      return CartAPI._trigger(CartAPI._events.add.complete, { item: e }), e;
    } catch (e) {
      CartAPI._trigger(CartAPI._events.add.error, { error: e }),
        CartAPI._onError(e);
    }
  }
  static async addFromForm(t) {
    if (t && t instanceof HTMLElement) {
      CartAPI._trigger(CartAPI._events.add.before);
      try {
        let e = await fetch("/cart/add.js", {
          method: "POST",
          body: theme.utils.serializeForm(t),
          ...CartAPI._fetchConfig,
        });
        e = await CartAPI._parse(e);
        return CartAPI._trigger(CartAPI._events.add.complete, { item: e }), e;
      } catch (e) {
        CartAPI._trigger(CartAPI._events.add.error, { error: e }),
          CartAPI._onError(e);
      }
    }
  }
  static async change(t) {
    CartAPI._trigger(CartAPI._events.update.before);
    try {
      let e = await fetch("/cart/change.js", {
        method: "POST",
        body: JSON.stringify(t),
        ...CartAPI._fetchConfig,
      });
      e = await CartAPI._parse(e);
      return CartAPI._trigger(CartAPI._events.update.complete, { cart: e }), e;
    } catch (e) {
      CartAPI._trigger(CartAPI._events.update.error, { error: e }),
        CartAPI._onError(e);
    }
  }
  static async update(t) {
    CartAPI._trigger(CartAPI._events.update.before);
    try {
      let e = await fetch("/cart/update.js", {
        method: "POST",
        body: JSON.stringify(t),
        ...CartAPI._fetchConfig,
      });
      e = await CartAPI._parse(e);
      return CartAPI._trigger(CartAPI._events.update.complete, { cart: e }), e;
    } catch (e) {
      CartAPI._trigger(CartAPI._events.update.error, { error: e }),
        CartAPI._onError(e);
    }
  }
  static async clear() {
    CartAPI._trigger(CartAPI._events.update.before);
    try {
      let e = await fetch("/cart/clear.js", {
        method: "POST",
        ...CartAPI._fetchConfig,
      });
      e = await CartAPI._parse(e);
      return CartAPI._trigger(CartAPI._events.update.complete, { cart: e }), e;
    } catch (e) {
      CartAPI._trigger(CartAPI._events.update.error, { error: e }),
        CartAPI._onError(e);
    }
  }
  static async _parse(e) {
    let t;
    try {
      t = await e.json();
    } catch (r) {
      t = null;
    }
    if (!e.ok || !t || t.status)
      throw Object.assign({ status: e.status }, t || {});
    return t;
  }
  static _onError(e) {
    console.error(
      `CartApi Error:
        status: ${e.status}
        message: ${e.message}
        description: ${e.description}
        errors: ` + JSON.stringify(e.errors)
    );
  }
  static getErrorMessage(e) {
    const t = (theme.strings && theme.strings.cartErrors) || {},
      r = (e) => (typeof e === "string" ? e.trim() : ""),
      s = ["cart error", "unprocessable entity", "bad_request", "bad request", "not found", "failed to fetch", "load failed", "networkerror when attempting to fetch resource."];
    if (!e) return t.generic || "There was an error while updating your cart. Please try again.";
    if (e.status === 404 || /cannot find variant/i.test(e.description || ""))
      return t.unavailable || "Sorry, this product is no longer available.";
    let i = r(e.description);
    if (i) return i;
    if (e.errors) {
      i =
        typeof e.errors === "string"
          ? e.errors
          : Object.values(e.errors).flat().filter((e) => typeof e === "string").join(" ");
      if (r(i)) return r(i);
    }
    i = r(e.message);
    if (i && !(e instanceof Error) && !s.includes(i.toLowerCase())) return i;
    if (e instanceof TypeError || e instanceof SyntaxError)
      return t.network || "We couldn't reach the store. Please check your connection and try again.";
    switch (e.status) {
      case 422:
        return t.quantity || "This item has limited availability. The maximum quantity available is already in your cart.";
      case 429:
        return t.rateLimit || "Too many requests. Please wait a moment and try again.";
    }
    return t.generic || "There was an error while updating your cart. Please try again.";
  }
  static _trigger(e, t = {}) {
    document.dispatchEvent(new CustomEvent(e, { detail: t }));
  }
}
(CartAPI._fetchConfig = {
  credentials: "same-origin",
  headers: {
    "Content-Type": "application/json",
    Accept: "application/javascript",
  },
}),
  (CartAPI._events = {
    update: {
      before: "cart:update:before",
      complete: "cart:update:complete",
      error: "cart:update:error",
    },
    fetch: {
      before: "cart:fetch:before",
      complete: "cart:fetch:complete",
      error: "cart:fetch:error",
    },
    add: {
      before: "cart:add:before",
      complete: "cart:add:complete",
      error: "cart:add:error",
    },
  });
class FocusableWidget extends HTMLElement {
  constructor(e = {}) {
    if ((super(), !e.name))
      throw new Error(`Focusable Widget(${e.name}) must have a name`);
    if (!this.id) throw new Error(e.name + " must have an id");
    if (!e.background)
      throw new Error(`Focusable Widget(${e.name}) must have a background`);
    (this._clientConfig = e), this._init(e);
  }
  open() {
    if (!this._isOpen) return this._closeFocusableWidgets(), this.toggle();
  }
  close() {
    if (this._isOpen) return this.toggle();
  }
  toggle() {
    return (
      theme.utils.prepareTransition(this),
      this._toggleClasses(),
      this._toggleAccessibilityAttributes(),
      this._isOpen
        ? (theme.utils.a11y.removeTrapFocus(this),
          this._unbindEvents(),
          this._dispatchEvents(this._events.close, ["close"]))
        : (this._bindEvents(),
          this._dispatchEvents(this._events.open, ["open"])),
      (this._isOpen = !this._isOpen),
      this
    );
  }
  reinitWidget() {
    this._init(this._clientConfig);
  }
  get isOpen() {
    return this._isOpen;
  }
  _init({
    name: e,
    background: t,
    widgetOpenClasses: s = [],
    bodyOpenClasses: r = [],
    attributes: i = {},
  }) {
    (this._name = e),
      (this._isOpen = !1),
      (this._selectors = {
        background: t,
        open: this.getAttribute("open"),
        close: this.getAttribute("close"),
      }),
      (this._nodes = {
        background: document.querySelector(this._selectors.background),
      }),
      (this._events = {
        open: ["focusable-widget:open", e + ":open", this.id + ":open"],
        close: ["focusable-widget:close", e + ":close", this.id + ":close"],
      }),
      (this._config = {
        open: document.querySelectorAll(this._selectors.open),
        close: document.querySelectorAll(this._selectors.close),
        attributes: {
          "data-focusable-widget": "true",
          "aria-hidden": "true",
          tabindex: 0,
          ...i,
        },
        widgetOpenClasses: ["open", this.getAttribute("open-class")].concat(s),
        bodyOpenClasses: [
          "js-focusable-widget-open",
          `js-${e}-open`,
          this.getAttribute("body-open-class"),
        ].concat(r),
      }),
      this._validateConfig(),
      this._addAccessibilityAttributes(),
      this._setupListeners();
  }
  _validateConfig() {
    if (
      ((this._config.open && this._config.open.length) ||
        console.warn(
          `WARN: ${this._name}: no open triggers found. Check your ${this._name} 'open' attribute, or add a trigger (if needed)`
        ),
        !this._config.close || !this._config.close.length)
    )
      throw new Error(
        `${this._name}: invalid close trigger selector, no elements found. Check your ${this._name} 'close' attribute`
      );
  }
  _setupListeners() {
    this._config.open.forEach((e) =>
      e.addEventListener("click", this._open.bind(this))
    ),
      this._config.close.forEach((e) =>
        e.addEventListener("click", this._close.bind(this))
      ),
      this.addEventListener("click", (e) => e.stopPropagation());
  }
  _open(e) {
    e && (e.preventDefault(), e.stopPropagation()), this.open();
  }
  _close = (e) => {
    e && (e.preventDefault(), e.stopPropagation()), this.close();
  };
  _closeFocusableWidgets() {
    const e = document.querySelectorAll("[data-focusable-widget].open");
    e.forEach((e) => e.close());
  }
  _dispatchEvents(e, t) {
    e.forEach((e) => document.dispatchEvent(new CustomEvent(e))),
      t && t.length && t.forEach((e) => this.dispatchEvent(new CustomEvent(e)));
  }
  _bindEvents() {
    document.addEventListener("keyup", this._onKeyUp),
      this._nodes.background.addEventListener("click", this._close),
      this._nodes.background.addEventListener("touchmove", this._onTouchMove, { passive: true });
  }
  _unbindEvents() {
    document.removeEventListener("keyup", this._onKeyUp),
      this._nodes.background.removeEventListener("click", this._close),
      this._nodes.background.removeEventListener(
        "touchmove",
        this._onTouchMove
      );
  }
  _toggleClasses() {
    this._isOpen
      ? (this._config.bodyOpenClasses.forEach((e) => {
        e && document.body.classList.remove(e);
      }),
        this._config.widgetOpenClasses.forEach((e) => {
          e && this.classList.remove(e);
        }))
      : (this._config.bodyOpenClasses.forEach((e) => {
        e && document.body.classList.add(e);
      }),
        this._config.widgetOpenClasses.forEach((e) => {
          e && this.classList.add(e);
        }));
  }
  _toggleAccessibilityAttributes() {
    this._isOpen
      ? (this.setAttribute("aria-hidden", "true"),
        this.setAttribute("tabindex", "-1"),
        this._config.open.forEach((e) =>
          e.setAttribute("aria-expanded", "false")
        ),
        this._config.close.forEach((e) =>
          e.setAttribute("aria-expanded", "false")
        ))
      : (this.setAttribute("aria-hidden", "false"),
        this.setAttribute("tabindex", "0"),
        this._config.open.forEach((e) =>
          e.setAttribute("aria-expanded", "true")
        ),
        this._config.close.forEach((e) =>
          e.setAttribute("aria-expanded", "true")
        ));
  }
  _addAccessibilityAttributes() {
    var e = (e) => {
      e.setAttribute("tabindex", "0"),
        e.setAttribute("aria-expanded", "false"),
        e.setAttribute("aria-controls", this.id);
    };
    for (const t in this._config.attributes)
      this.setAttribute(t, this._config.attributes[t]);
    this._config.open.forEach(e), this._config.close.forEach(e);
  }
  _onKeyUp = (e) => {
    "ESCAPE" === e.code.toUpperCase() && this.close();
  };
  _onTouchMove = () => !1;
}
(theme.CartAPI = window.CartAPI = CartAPI),
  (theme.AjaxCart = document.querySelector("s-ajaxcart"));
class Drawer extends FocusableWidget {
  constructor() {
    super({ name: "drawer", background: ".drawer-background" });
    var e = this.getAttribute("position");
    if (!e) throw new Error("drawer must have position attribute");
    (this._position = e),
      this._config.bodyOpenClasses.push(`js-drawer-${e}-open`);
  }
  get position() {
    return this._position;
  }
}
class Modal extends FocusableWidget {
  constructor() {
    super({ name: "modal", background: ".modal-background" }),
      this._config.bodyOpenClasses.push("js-modal-open");
  }
}
class Accordion extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        accordionItem: "[js-accordion-item]",
        accordionHeader: "[js-accordion-header]",
        accordionContent: "[js-accordion-content]",
      });
  }
  connectedCallback() {
    (this.buttons = this.querySelectorAll(this._selectors.accordionHeader)),
      this.buttons.forEach((e) => {
        e.addEventListener("click", this._toggleContent);
      }),
      (this.content = this.querySelectorAll(this._selectors.accordionContent)),
      this.content.forEach((e) => {
        e.setAttribute("style", `height: ${e.scrollHeight}px;`);
      });
  }
  _toggleContent = (e) => {
    const t = e.currentTarget;
    e = t.nextElementSibling;
    const s = t.parentElement,
      r = this.querySelectorAll(this._selectors.accordionItem);
    "false" == t.getAttribute("aria-expanded")
      ? (t.setAttribute("aria-expanded", !0),
        theme.utils.prepareTransition(e),
        this._expandSection(e),
        this.dataset.manualCollapse ||
        r.forEach((e) => {
          if (e !== s) {
            const t = e.querySelector(this._selectors.accordionHeader);
            e = t.nextElementSibling;
            "true" == t.getAttribute("aria-expanded") &&
              (t.setAttribute("aria-expanded", !1),
                theme.utils.prepareTransition(e),
                this._collapseSection(e));
          }
        }))
      : (t.setAttribute("aria-expanded", !1),
        theme.utils.prepareTransition(e),
        this._collapseSection(e));
  };
  _collapseSection(e) {
    var t = e.scrollHeight,
      s = e.style.transition;
    (e.style.transition = ""),
      requestAnimationFrame(function () {
        (e.style.height = t + "px"),
          (e.style.transition = s),
          requestAnimationFrame(function () {
            e.style.height = "0px";
          });
      }),
      e.setAttribute("aria-hidden", !0),
      e.classList.add("hidden");
  }
  _expandSection(e) {
    var t = e.scrollHeight;
    e.classList.remove("hidden"),
      (e.style.height = t + "px"),
      e.setAttribute("aria-hidden", !1);
  }
}
class Tabs extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    (this._elements = {
      tabs: this.querySelectorAll("[js-tab]"),
      tabList: this.querySelectorAll("[js-tab-list]"),
      tabPanel: this.querySelectorAll("[js-tab-panel]"),
    }),
      this._tabEventListener(),
      this._tabListListener();
  }
  _tabEventListener = () => {
    this._elements.tabs.forEach((e) => {
      e.addEventListener("click", this._showTabPanel);
    });
  };
  _showTabPanel = (e) => {
    e.stopImmediatePropagation();
    const t = this._elements["tabs"];
    for (let e = 0; e < t.length; e++)
      t[e].setAttribute("aria-selected", "false");
    e.target.setAttribute("aria-selected", "true");
    var s = e.target.getAttribute("aria-controls");
    let r = e.target.closest("[js-tabs]");
    var i = r.querySelectorAll("[role=tabpanel]");
    for (let e = 0; e < i.length; e++)
      i[e].id == s
        ? i[e].setAttribute("aria-hidden", "false")
        : i[e].setAttribute("aria-hidden", "true");
  };
  _tabListListener = () => {
    const e = this._elements["tabList"];
    e.forEach((e) => e.addEventListener("keydown", this._tabListKeydown));
  };
  _tabListKeydown = (e) => {
    37 == e.keyCode &&
      ($("[aria-selected=true]").prev().click().focus(), e.preventDefault()),
      38 == e.keyCode &&
      ($("[aria-selected=true]").prev().click().focus(), e.preventDefault()),
      39 == e.keyCode &&
      ($("[aria-selected=true]").next().click().focus(), e.preventDefault()),
      40 == e.keyCode &&
      ($("[aria-selected=true]").next().click().focus(), e.preventDefault());
  };
}
class AjaxCart extends HTMLElement {
  constructor() {
    super(),
      this._Ajaxcart,
      (this._selectors = {
        drawer: "[js-cart-drawer]",
        afterpay: ".cart-afterpay",
        customUpsellData: "#AjaxCartCustomUpsellData[js-custom-upsell-data]",
        upsellData: "#AjaxCartUpsellData[js-upsell-data]",
        counter: "[js-cart-counter]",
      }),
      (this._internalEvents = {
        fetch: "ajaxcart:fetch",
        open: "ajaxcart:open",
        close: "ajaxcart:close",
        refreshUpsell: "ajaxcart:upsell:refresh",
      }),
      (this._config = {
        shippingThreshold: "true" === this.dataset.shippingThreshold,
      }),
      (this._drawer = document.querySelector(this._selectors.drawer)),
      this._initAjaxCart();
  }
  fetch(e = !0) {
    this.dispatchEvent(
      new CustomEvent(this._internalEvents.fetch, { detail: { openDrawer: e } })
    );
  }
  open(e = !0) {
    this.dispatchEvent(
      new CustomEvent(this._internalEvents.open, { detail: { fetch: e } })
    );
  }
  close() {
    this.dispatchEvent(new CustomEvent(this._internalEvents.close));
  }
  refreshUpsell() {
    this.dispatchEvent(new CustomEvent(this._internalEvents.refreshUpsell));
  }
  _initAjaxCart() {
    var e = new URLSearchParams(window.location.search).get("viewcart");
    (this._Ajaxcart = Vue.createApp(this._ajaxCartApp())),
      this._registerComponents(),
      this._Ajaxcart.mount("#AjaxCartTemplate"),
      this.classList.remove("hidden"),
      this._drawer.reinitWidget(),
      "true" === e && this.open(),
      (this._Ajaxcart.config.compilerOptions.isCustomElement = (e) =>
        "s-carousel" === e);
  }
  _ajaxCartApp() {
    const r = this;
    return {
      delimiters: ["${", "}"],
      data() {
        return {
          drawer: r._drawer,
          shippingThreshold: r._config.shippingThreshold,
          cart: null,
          error: !1,
          counter: document.querySelector(r._selectors.counter),
          cartHeight: !1,
          loading: { cart: !1, item: !1 },
        };
      },
      computed: {
        handlesInCart() {
          return this.cart.items.reduce(
            (e, t) => ({ ...e, [t.handle]: !0 }),
            {}
          );
        },
      },
      watch: {
        cart() {
          this.cartHeight || this.$nextTick(this.updateCartHeight);
        },
      },
      updated() {
        const e = document.querySelector(r._selectors.afterpay);
        if (e) {
          const s = e.querySelector("span");
          var t = theme.utils.formatMoney(this.cart.total_price / 4);
          0 == this.cart.total_price
            ? e.classList.add("hidden")
            : e.classList.contains("hidden") && e.classList.remove("hidden"),
            (s.innerHTML = t);
        }
        this.counter &&
          (this.cart || (this.counter.innerHTML = 0),
            (this.counter.innerHTML = this.cart.item_count));
      },
      mounted() {
        const e = document.querySelector("s-ajaxcart");
        e.addEventListener("ajaxcart:fetch", this.onFetch),
          e.addEventListener("ajaxcart:open", this.onOpen),
          e.addEventListener("ajaxcart:close", this.onClose),
          document.addEventListener("CartDrawer:open", async () => {
            this.cart || (this.cart = await this.fetchCart());
          }),
          document.addEventListener("CartDrawer:close", () => {
            this.error && (this.error = !1);
          }),
          document.addEventListener(
            "cart:add:complete",
            this.onCartAddComplete
          ),
          document.addEventListener(
            "cart:update:complete",
            this.onCartUpdateComplete
          ),
          document.addEventListener("cart:update:error", this.onCartError),
          document.addEventListener("cart:add:error", this.onCartError);
      },
      methods: {
        async fetchCart() {
          this.loading.cart = !0;
          var e = await theme.CartAPI.get();
          return (this.loading.cart = !1), e;
        },
        async updateItemQuantity({ line: e, quantity: t }) {
          t < 0 ||
            ((this.error = !1),
              (this.loading.item = e - 1),
              (e = await theme.CartAPI.change({ line: e, quantity: t })),
              (this.cart = e || (await this.fetchCart())),
              (this.loading.item = !1));
        },
        async removeItem(e) {
          (this.loading.item = e - 1),
            await this.updateItemQuantity({ line: e, quantity: 0 }),
            (this.loading.item = !1);
        },
        async addToCart({ variantId: e, quantity: t }) {
          this.loading.cart = !0;
          e = await theme.CartAPI.add({ id: e, quantity: t });
          return (this.loading.cart = !1), e;
        },
        async onCartUpdateComplete({ detail: e }) {
          e = e.cart;
          (this.error = !1),
            (this.cart = e || (await this.fetchCart())),
            this.drawer.isOpen || this.drawer.open();
        },
        async onCartAddComplete() {
          (this.error = !1),
            (this.cart = await this.fetchCart()),
            this.drawer.isOpen || this.drawer.open();
        },
        async onFetch({ detail: e }) {
          var { openDrawer: e = !1 } = e;
          (this.cart = await this.fetchCart()), e && this.drawer.open();
        },
        async onOpen({ detail: e }) {
          var { fetch: e = !0 } = e;
          e && (this.cart = await this.fetchCart()), this.drawer.open();
        },
        onClose() {
          this.drawer.close();
        },
        async onCartError({ detail: e }) {
          e = e && e.error;
          if (!e) return;
          const t = theme.CartAPI.getErrorMessage(e);
          this.cart = (await this.fetchCart()) || this.cart;
          this.error = t;
          if (!this.drawer.isOpen) this.drawer.open();
        },
        formatMoney(e) {
          return theme.utils.formatMoney(e);
        },
        updateCartHeight() {
          var e = this.$refs.cartHeader.clientHeight,
            t = this.$refs.cartFooter.clientHeight;
          this.cartHeight = `calc(100vh - ${(e + t) / 16}rem)`;
        },
        closeDrawer() {
          this.drawer.close();
        },
      },
    };
  }
  _registerComponents() {
    var e = document.querySelector(this._selectors.upsellData),
      t = document.querySelector(this._selectors.customUpsellData),
      s = document.querySelector("#AjaxCartShippingThreshold").dataset
        .threshold;
    this._registerComponent(
      "line-item",
      "#AjaxCartLineItem",
      this._Components.LineItem()
    ),
      this._registerComponent(
        "shipping-threshold",
        "#AjaxCartShippingThreshold",
        this._Components.ShippingThreshold(s)
      ),
      this._registerComponent(
        "custom-upsell",
        "#AjaxCartCustomUpsell",
        this._Components.CustomUpsell(t ? JSON.parse(t.innerHTML) : [])
      ),
      this._registerComponent(
        "custom-upsell-product",
        "#AjaxCartCustomUpsellProduct",
        this._Components.CustomUpsellProduct()
      ),
      this._registerComponent(
        "upsell-products",
        "#AjaxCartUpsell",
        this._Components.UpsellProducts(e ? JSON.parse(e.innerHTML) : [])
      ),
      this._registerComponent(
        "upsell-product",
        "#AjaxCartUpsellProduct",
        this._Components.UpsellProduct()
      ),
      this._registerComponent(
        "skeleton-item",
        "#AjaxCartSkeletonItem",
        this._Components.SkeletonItem()
      );
  }
  _registerComponent(e, t, s) {
    const r = document.querySelector(t);
    this._Ajaxcart.component(e, { template: r.innerHTML, ...s }), r.remove();
  }
  _Components = {
    mixins() {
      return {
        methods: {
          formatMoney(e) {
            return theme.utils.formatMoney(e);
          },
          formatTitle(e) {
            var t;
            return e.includes(" – ")
              ? (t = e.split(" – "))[0] + "\n" + t[1]
              : e;
          },
          truncateString(e, t, s = "...") {
            return e.length < t ? e : e.substring(0, t) + s;
          },
          arrayIntoBooleanObject(e, s) {
            return e.reduce((e, t) => ({ ...e, [s ? t[s] : t]: !0 }), {});
          },
        },
      };
    },
    LineItem() {
      return {
        delimiters: ["${", "}"],
        mixins: [this.mixins()],
        props: ["item", "line", "loading"],
        emits: ["removeItem", "updateItemQuantity"],
        computed: {
          isGWP() {
            return this.item.properties && this.item.properties._custom_upsell;
          },
        },
      };
    },
    ShippingThreshold(e) {
      return {
        delimiters: ["${", "}"],
        mixins: [this.mixins()],
        props: ["cart"],
        data() {
          return { threshold: e };
        },
        computed: {
          amountForFreeShipping() {
            if (this.cart) return this.threshold - this.cart.total_price;
          },
          progress() {
            if (this.cart)
              return (this.cart.total_price / this.threshold) * 100;
          },
          unlocked() {
            return this.amountForFreeShipping <= 0;
          },
        },
      };
    },
    CustomUpsell(e) {
      const r = {
        CART_AMOUNT: "CART_AMOUNT",
        COLLECTION_ITEMS: "COLLECTION_ITEMS",
      };
      return {
        delimiters: ["${", "}"],
        mixins: [this.mixins()],
        props: ["cart", "handlesInCart"],
        data() {
          return { products: e, removeQueue: [], unlockedTable: {} };
        },
        computed: {
          cartTotalAmount() {
            return this.cart.total_price;
          },
          productsToAdd() {
            return this.products.filter(
              (e) => !this.handlesInCart[e.product.handle]
            );
          },
          cartHasCustomUpsellItem() {
            return (
              this.cart &&
              this.cart.items.some(
                (e) => e.properties && e.properties._custom_upsell
              )
            );
          },
        },
        watch: {
          cart: {
            deep: !0,
            handler() {
              this.updateUpsell();
            },
          },
        },
        mounted() {
          this.updateUpsell();
        },
        methods: {
          async updateUpsell() {
            (this.removeQueue = this.buildRemoveQueue()),
              (this.unlockedTable = this.buildUnlockedTable()),
              await this.remove(this.removeQueue);
          },
          buildRemoveQueue() {
            return this.products.filter((e) => {
              if (!this.isInCart(e)) return !1;
              switch (e.threshold_type) {
                case r.CART_AMOUNT:
                  return (
                    this.cartTotalAmount < parseInt(e.threshold_amount, 10)
                  );
                case r.COLLECTION_ITEMS:
                  return (
                    this.getCollectionItemsCountInCart(
                      e.target_collection_handle
                    ) < parseInt(e.threshold_amount, 10)
                  );
                default:
                  return !1;
              }
            });
          },
          buildUnlockedTable() {
            const s = {};
            return (
              this.products.forEach((e) => {
                let t = !1;
                switch (e.threshold_type) {
                  case r.CART_AMOUNT:
                    this.cartTotalAmount >= parseInt(e.threshold_amount, 10) &&
                      (t = !0);
                    break;
                  case r.COLLECTION_ITEMS:
                    this.getCollectionItemsCountInCart(
                      e.target_collection_product_handles
                    ) >= parseInt(e.threshold_amount, 10) && (t = !0);
                }
                s[e.product.handle] = t;
              }),
              s
            );
          },
          remove(e) {
            if (e.length) {
              const t = e.map((e) => this.getLineItem(e)).filter((e) => e);
              e = { updates: t.reduce((e, t) => ({ ...e, [t.key]: 0 }), {}) };
              return CartAPI.update(e);
            }
          },
          isInCart(e) {
            return this.handlesInCart[e.product.handle];
          },
          getLineItem(t) {
            return this.cart.items.find((e) => e.handle === t.product.handle);
          },
          getCollectionItemsCountInCart(e) {
            const s = new Set(e);
            return this.cart.items.reduce(
              (e, t) => (s.has(t.handle) ? e + 1 : e),
              0
            );
          },
        },
      };
    },
    CustomUpsellProduct() {
      return {
        delimiters: ["${", "}"],
        mixins: [this.mixins()],
        props: ["upsellProduct", "unlocked"],
        data() {
          return {
            selectedVariant: null,
            activeOption: null,
            activeOptionValue: null,
            showOptionSelector: !1,
          };
        },
        computed: {
          product() {
            return this.upsellProduct.product;
          },
        },
        watch: {
          activeOptionValue() {
            (this.selectedVariant = this.getVariantFromOption()),
              this.addToCart();
          },
        },
        created() {
          (this.selectedVariant = this.getFirstAvailableVariant()),
            (this.activeOption = this.upsellProduct.options_with_values.find(
              (e) => "size" === e.name.toLowerCase()
            ));
        },
        methods: {
          addToCart() {
            this.selectedVariant &&
              theme.CartAPI.add({
                id: this.selectedVariant.id,
                quantity: 1,
                properties: {
                  _custom_upsell: !0,
                  _custom_upsell_type: this.upsellProduct.threshold_type,
                  _custom_upsell_threshold: this.upsellProduct.threshold_amount,
                  _custom_upsell_target_collection:
                    this.upsellProduct.target_collection_handle,
                },
              });
          },
          getFirstAvailableVariant() {
            if (!this.product) return null;
            let t;
            for (let e = 0; e < this.product.variants.length; e++) {
              var s = this.product.variants[e];
              if (s.available) {
                t = s;
                break;
              }
            }
            return t;
          },
          getVariantFromOption() {
            return this.product.variants.find((e) => {
              return (
                e["option" + this.activeOption.position] ===
                this.activeOptionValue
              );
            });
          },
        },
      };
    },
    UpsellProducts(e = []) {
      return {
        delimiters: ["${", "}"],
        mixins: [this.mixins()],
        props: ["cart", "handlesInCart"],
        emits: ["addToCart"],
        data() {
          return {
            products: [],
            defaultProducts: [],
            taggedProducts: {},
            limit: theme.config.upsellLimit,
            upsellTags: theme.upsellTags || [],
            fetching: !1,
            currentSlide: 0,
          };
        },
        computed: {
          tagsInCart() {
            if (this.cart && this.cart.items) {
              let t = [];
              return (
                this.cart.items.forEach((e) => {
                  e?.properties?._tag && t.push(e.properties._tag);
                }),
                this.arrayIntoBooleanObject(t)
              );
            }
          },
          handlesInUpsell() {
            return this.getHandles(this.products);
          },
        },
        created() {
          this.processParsedProducts(e);
        },
        mounted() {
          const e = document.querySelector("s-ajaxcart");
          e.addEventListener("ajaxcart:upsell:refresh", this.refreshUpsell),
            this.refreshUpsell();
        },
        watch: {
          cart(s, e) {
            return e && s && e?.items && s?.items
              ? void (
                e.items.some((e, t) => s?.items[t]?.handle !== e?.handle) &&
                this.refreshUpsell()
              )
              : this.refreshUpsell();
          },
        },
        updated() {
          setTimeout(() => this.initSlider(), 1);
        },
        methods: {
          initSlider() {
            var e = document.querySelector("[js-upsell-slider]");
            this.slider && this.slider.destroy(),
              e &&
              ((this.slider = new Flickity(e, {
                contain: !0,
                adaptiveHeight: !0,
                imagesLoaded: !0,
                pageDots: !1,
                freeScroll: !0,
              })),
                this.slider.on("change", (e) => {
                  this.currentSlide = e;
                }),
                this.products.length && this.slider.select(this.currentSlide));
          },
          async refreshUpsell() {
            if (this.cart && this.cart.items)
              try {
                this.removeUpsellProductsInCart(),
                  await this.fetchUpsell(),
                  this.products.length < this.limit &&
                  (this.fillUpsellWithTaggedProducts(),
                    this.fillUpsellWithDefaults());
              } catch (e) {
              }
          },
          async fetchUpsell() {
            this.products = (
              await this.fetchUpsellHandles(this.getUpsellHandles())
            ).filter((e) => e.available);
          },
          processParsedProducts(e) {
            e &&
              e.forEach((e) => {
                e.product &&
                  e.tag &&
                  e.product.available &&
                  ((e.product.options = e.product_options),
                    "__DEFAULT__" === e.tag
                      ? this.defaultProducts.push(e.product)
                      : (this.taggedProducts[e.tag] = e.product));
              });
          },
          fillUpsellWithTaggedProducts() {
            for (
              let e = 0;
              e < this.upsellTags.length &&
              !(this.products.length >= this.limit);
              e++
            ) {
              var t = this.upsellTags[e];
              this.tagsInCart[t] &&
                this.taggedProducts[t] &&
                this.addToUpsellIfNotRedundant(this.taggedProducts[t]);
            }
          },
          fillUpsellWithDefaults() {
            for (
              let e = 0;
              e < this.defaultProducts.length &&
              !(this.products.length >= this.limit);
              e++
            ) {
              var t = this.defaultProducts[e];
              this.handlesInUpsell[t.handle] ||
                this.addToUpsellIfNotRedundant(t);
            }
          },
          async fetchUpsellHandles(e) {
            if (!e && !e.length) return Promise.resolve([]);
            let t = [];
            this.fetching = !0;
            try {
              t = await Promise.all(
                e.map((e) => fetch(`/products/${e}.js`))
              ).then((e) => Promise.all(e.map((e) => e.json())));
            } catch (e) {
              console.error(e);
            } finally {
              return (this.fetching = !1), t;
            }
          },
          getUpsellHandles() {
            if (this.cart && this.cart.items) {
              let t,
                s = 0;
              const r = [];
              for (
                let e = 0;
                e < this.cart.items.length && !(s > this.limit);
                e++
              )
                (t = this.cart.items[e]?.properties?.upsell?.handle) &&
                  !this.handlesInCart[t] &&
                  (r.push(t), s++);
              return [...new Set(r)];
            }
          },
          removeUpsellProductsInCart() {
            let e = this.products.length;
            for (var t; e--;)
              (t = this.products[e].handle),
                this.handlesInCart[t] && this.products.splice(e, 1);
          },
          addToUpsellIfNotRedundant(e) {
            e &&
              e.handle &&
              (this.handlesInUpsell[e.handle] ||
                this.handlesInCart[e.handle] ||
                (this.products.push(e), (this.handlesInUpsell[e.handle] = !0)));
          },
          getHandles(e) {
            return Array.isArray(e)
              ? this.arrayIntoBooleanObject(e, "handle")
              : {};
          },
          changeSlide(e) {
            this.currentSlide = e;
          },
        },
      };
    },
    UpsellProduct() {
      return {
        delimiters: ["${", "}"],
        mixins: [this.mixins()],
        props: ["product"],
        data() {
          return {
            selectedVariant: this.getFirstAvailableVariant(),
            options: { 1: null, 2: null, 3: null },
            baseSwatchURL: "//cdn.shopify.com/s/files/1/0069/7692/files/",
          };
        },
        watch: {
          selectedVariant(e) {
            if (e && e.featured_media) {
              const t = this.$refs["carousel"];
              t.selectSlide(e.featured_media.position - 1);
            }
          },
        },
        mounted() {
          (this.options[1] = this.selectedVariant.option1),
            (this.options[2] = this.selectedVariant.option2),
            (this.options[3] = this.selectedVariant.option3);
        },
        methods: {
          getFileName(e) {
            return (
              this.baseSwatchURL +
              e
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/-$/, "")
                .replace(/^-/, "") +
              ".png"
            );
          },
          addToCart() {
            this.selectedVariant &&
              theme.CartAPI.add({
                id: this.selectedVariant.id,
                quantity: 1,
                upsell: { tags: this.product.tags, handle: null },
              });
          },
          getFirstAvailableVariant() {
            if (!this.product) return null;
            let t;
            for (let e = 0; e < this.product.variants.length; e++) {
              var s = this.product.variants[e];
              if (s.available) {
                t = s;
                break;
              }
            }
            return t;
          },
          onOptionChange() {
            this.selectedVariant = this.getVariantFromOptions(this.options);
          },
          getVariantFromOptions(i) {
            let o = !1;
            return (
              this.product.variants.forEach((e) => {
                let t = !0;
                for (const r in i) {
                  var s = "option" + r;
                  t = t && i[r] === e[s];
                }
                t && (o = e);
              }),
              o
            );
          },
        },
      };
    },
    SkeletonItem() {
      return {};
    },
  };
}
class Carousel extends HTMLElement {
  constructor() {
    super(),
      (this.selectors = {
        carousel: "[js-carousel]",
        productCarousel: "[js-product-carousel]",
        slide: "[js-carousel-slide]",
        dots: "[js-carousel-dots]",
        prevButton: "[js-prev-button]",
        nextButton: "[js-next-button]",
      }),
      (this.options = {
        imagesLoaded: !0,
        contain: !0,
        pageDots: !1,
        prevNextButtons: !1,
        cellSelector: this.selectors.slide,
        wrapAround: !0,
        on: { ready: this._onReady },
      });
  }
  connectedCallback() {
    var e = this.dataset.options ? JSON.parse(this.dataset.options) : {};
    (this.options = Object.assign(this.options, e)), this._initCarousel();
  }
  _showAlt() {
    var s = this.selectors.slide,
      r = document.querySelector(".image-size__container");
    this.carousel.on("select", function (e) {
      var t,
        e = document
          .querySelector(s + '[data-index="' + e + '"]')
          .getAttribute("data-alt");
      e.includes("Model")
        ? ((t = (e = e.split("Model:"))[1].split(" ")[1]),
          (e = e[1].split("Size:")[1]),
          r.classList.remove("hidden"),
          (r.innerHTML = `<p class="image-size py-8">Model is ${t} and wearing size ${e}</p>`))
        : r.classList.add("hidden");
    });
  }
  _showSlides() {
    this.carousel.element.classList.contains("hero-carousel--hidden") &&
      this.carousel.element.classList.remove("hero-carousel--hidden");
  }
  _initCarousel() {
    (this.dots = this.querySelector(this.selectors.dots)),
      this.dots && (this.eachDot = this.dots.querySelectorAll("button")),
      (this.prevButton = this.querySelector(this.selectors.prevButton)),
      (this.nextButton = this.querySelector(this.selectors.nextButton)),
      void 0 !== this.dataset.init
        ? enquire.register(theme.config.mediaQueries[this.dataset.init], {
          match: function () {
            (this.carousel = new Flickity(
              this.querySelector(this.selectors.carousel),
              this.options
            )),
              this._initCarouselControls(),
              window.location.pathname.includes("/products/") &&
              this._showAlt(),
              this.dataset.showSlides && this._showSlides();
          }.bind(this),
          unmatch: function () {
            this.carousel.element.classList.contains("flickity-enabled") &&
              this.carousel.destroy();
          }.bind(this),
        })
        : ((this.carousel = new Flickity(
          this.querySelector(this.selectors.carousel),
          this.options
        )),
          this._initCarouselControls(),
          window.location.pathname.includes("/products/") && this._showAlt(),
          void 0 !== this.dataset.showSlides && this._showSlides()),
      this.carousel && document.addEventListener("lazyLoad", this.carousel.resize());
  }
  _initCarouselControls() {
    this.dots &&
      (this.carousel.on("change", this._updateDots),
        this.carousel.on("select", this._updateDots),
        this.eachDot.forEach((e) => {
          e.addEventListener("click", this._onDotClick);
        })),
      this.prevButton &&
      this.prevButton.addEventListener("click", this._onPrevClick),
      this.nextButton &&
      this.nextButton.addEventListener("click", this._onNextClick),
      this._registerCustomListeners();
  }
  _registerCustomListeners() {
    if (this.dots) {
      const t = (e) => e.classList.contains("product-carousel__dot");
      this.querySelector(this.selectors.dots).addEventListener(
        "resetDotListeners",
        () => {
          this.eachDot.forEach((e) => {
            e.removeEventListener("click", this._onDotClick);
          });
          const e = Array.from(this.eachDot).filter(t);
          e.forEach((e) => {
            e.addEventListener("click", this._onDotClick);
          });
        }
      );
    }
  }
  selectSlide(e) {
    this.carousel.select(e, !0, !1);
  }
  _onReady = () => {
    this.dots && this._updateDots(0);
  };
  _updateDots = (t) => {
    this.eachDot.forEach((e) => {
      e.classList.remove("selected");
    });
    const e = Array.from(this.eachDot).filter(
      (e) => !e.classList.contains("hidden")
    ),
      s = e.filter((e) => e.getAttribute("data-index") == t)[0];
    s.classList.add("selected");
  };
  _onDotClick = (e) => {
    var e = e.currentTarget,
      t = e.dataset.index;
    this.carousel.select(t, !0, !1),
      this.eachDot.forEach((e) => {
        e.classList.remove("selected");
      }),
      e.classList.add("selected");
  };
  _onPrevClick = () => {
    this._checkCell(), this.carousel.previous(!0, !0);
  };
  _onNextClick = () => {
    this._checkCell(), this.carousel.next(!0, !0);
  };
  _checkCell = () => {
    this.carousel.on("cellSelect", () => {
      this.carousel.cells[this.carousel.selectedIndex - 1]
        ? this.carousel.cells[this.carousel.selectedIndex + 1]
          ? (this.prevButton.removeAttribute("disabled"),
            this.nextButton.removeAttribute("disabled"))
          : this.nextButton.setAttribute("disabled", "disabled")
        : this.prevButton.setAttribute("disabled", "disabled");
    });
  };
}
class ProductOptions extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        singleOptionSelector: "[js-product-option-selector]",
        carousel: "[js-carousel]",
      }),
      (this._events = {
        change: "variant:change",
        fetchBefore: "variant:fetch:before",
        fetchAfter: "variant:fetch:after",
        fetchError: "variant:fetch:error",
      }),
      (this.colorSpan = document.querySelector(
        ".product-swatch-colors__selected"
      ));
  }
  connectedCallback() {
    (this._handle = this.dataset.productHandle),
      (this._checkAvailableOptions = !this.hasAttribute(
        "data-disable-option-binding"
      )),
      (this._optionSelectors = [
        ...this.querySelectorAll(this._selectors.singleOptionSelector),
      ]),
      this.carousel && (this.carousel = new Flickity(this._selectors.carousel)),
      this._checkAvailableOptions &&
      ((this._availableValues = {}),
        (this._lastSelectedOption = null),
        (this._option1Values = this._option2Values = null)),
      this.dataset.product &&
      (e = document.getElementById(this.dataset.product)) &&
      (this._product = JSON.parse(e.innerHTML)),
      this._product &&
      ((this._currentVariant = this._getVariantFromOptions()),
        this._checkAvailableOptions &&
        this._setupOptionsAvailability(this._product)),
      this.dataset.masterSelectId &&
      ((this._masterSelect = document.getElementById(
        this.dataset.masterSelectId
      )),
        this._updateMasterSelect(this._currentVariant)),
      (this._product || this._handle) && this._setupListeners();
    var e = document.querySelectorAll(".color-swatch");
    e &&
      e.forEach((e) => {
        e.checked && (this.colorSpan.innerHTML = e.defaultValue);
      });
  }
  set product(e) {
    (this._product = e), this.disconnectedCallback(), this.connectedCallback();
  }
  _setupListeners() {
    this._optionSelectors.forEach((e) =>
      e.addEventListener("change", this._handleOptionChange)
    );
  }
  _getVariantFromOptions() {

    if (this._product) {

      // Get current options (both option1 and option2)
      const currentOptions = this._getCurrentOptions();

      // Get the variants from the product
      const variants = this._product["variants"];


      const matchingVariant = variants.find((variant) => {
        const allMatch = currentOptions.every((option) => {
          const variantValue = String(variant[option.index]).trim().replace(/"/g, '');
          const optionValue = String(option.value).trim().replace(/"/g, '');
          return optionValue === variantValue;
        });
        return allMatch;
      });

      // Update the hidden input scoped to the closest form — covers PDP, quick-add drawers,
      // and recommendation sections. Scoping to the form prevents a single global
      // getElementById from clobbering the wrong input when multiple product forms exist.
      if (matchingVariant) {
        const closestForm = this.closest('form');
        const hiddenInput = closestForm
          ? closestForm.querySelector('[name="product-selected-id"]')
          : document.getElementById('product-selected-id');
        if (hiddenInput) {
          hiddenInput.value = matchingVariant.id;
        }
      }
      return matchingVariant;
    }
   
  }
  _getCurrentOptions() {
    return this._optionSelectors
      .map((e) => {
        var t = e.getAttribute("type");
        const s = {};
        return (
          (("radio" !== t && "checkbox" !== t) || !!e.checked) &&
          ((s.value = e.value), (s.index = e.dataset.index), s)
        );
      })
      .filter((e) => e);
  }
  _setupOptionsAvailability(e) {
    (this._option1Values = new Set()),
      (this._option2Values = new Set()),
      e.variants.forEach((e) => {
        var { option1: e, option2: t } = e;
        e && this._option1Values.add(e), t && this._option2Values.add(t);
      }),
      (this._option1Values = [...this._option1Values]),
      (this._option2Values = [...this._option2Values]),
      (this._availableValues = this._getAvailableValues(this._product)),
      this._refreshValuesUI(this._availableValues);
  }
  _handleOptionChange = async (t) => {
    if (this._product) return this._updateVariant(t);
    // Reuse the in-flight request instead of firing one per click. This is promise
    // reuse, not a debounce: every queued handler still runs _updateVariant, which
    // re-reads live :checked state, so resolution semantics are unchanged.
    if (!this._productRequest) {
      this._productRequest = this._fetchProduct(this._handle);
    }
    return this._productRequest
      .then((e) => {
        if (!e) {
          this._productRequest = null; // failed fetch: allow a retry
          return;
        }
        (this._product = e),
          this._checkAvailableOptions &&
          this._setupOptionsAvailability(this._product),
          this._updateVariant(t);
      })
      .catch(() => {
        this._productRequest = null;
      });
  };
  _updateVariant(e) {
    this._lastSelectedOption = {
      key: e.target.dataset.index,
      value: e.target.value,
    };
    var t,
      s,
      e = this._getVariantFromOptions();
    this.dispatchEvent(
      new CustomEvent(this._events.change, {
        detail: { variant: e, priceChange: this._hasPriceChanged(e) },
        bubbles: !0,
      })
    ),
      this._checkAvailableOptions &&
      ((t = this._getAvailableValues(
        this._product,
        this._lastSelectedOption
      )),
        (s = this._getDifference(this._availableValues, t)),
        this._refreshValuesUI(s),
        (this._availableValues = t)),
      this.hasAttribute("data-enable-history-state") &&
      this._updateHistoryState(e),
      this._masterSelect && this._updateMasterSelect(e),
      this.colorSpan && (this.colorSpan.innerHTML = e.option2),
      (this._currentVariant = e);
  }
  _hasPriceChanged(e) {
    if (e)
      return !(
        e.price === this._currentVariant?.price &&
        e.compare_at_price === this._currentVariant?.compare_at_price
      );
  }
  _getAvailableValues(s, r) {
    if (r) {
      const i = {},
        t = ["option1", "option2"];
      var e = { option1: this._option1Values, option2: this._option2Values };
      const o = t.find((e) => e !== r.key),
        n = e[o];
      return (
        n.forEach((t) => {
          var e = s.variants.findIndex(
            (e) => e[r.key] === r.value && e[o] === t && e.available
          );
          i[t] = 0 <= e;
        }),
        i
      );
    }
    return s.variants.reduce((e, t) => {
      var { option1: t, option2: s } = t;
      return t && (e[t] = !0), s && (e[s] = !0), e;
    }, {});
  }
  _refreshValuesUI(e) {
    for (const s in e) {
      const r = this.querySelector(`[value="${s}"]`);
      var t;
      r &&
        ((r.disabled = !e[s]),
          "radio" === (t = r.getAttribute("type")) || "checkbox" === t
            ? (r.checked = !1)
            : (r.selected = !1));
    }
  }
  _updateHistoryState(e) {
    history.replaceState &&
      e &&
      ((e =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?variant=" +
        e.id),
        window.history.replaceState({ path: e }, "", e));
  }
  _updateMasterSelect(e) {
    e && (this._masterSelect.value = e.id);
  }
  async _fetchProduct(e) {
    this.dispatchEvent(
      new CustomEvent(this._events.fetchBefore, { bubbles: !0 })
    );
    try {
      const s = await fetch(`/products/${e}.js`);
      var t = await s.json();
      return (
        this.dispatchEvent(
          new CustomEvent(this._events.fetchAfter, { bubbles: !0 })
        ),
        t
      );
    } catch (e) {
      this.dispatchEvent(
        new CustomEvent(this._events.fetchError, {
          details: { error: e },
          bubbles: !0,
        })
      );
    }
  }
  _getDifference(e, t) {
    const s = {};
    for (const r in t) t[r] !== e?.[r] && (s[r] = t[r]);
    return s;
  }
  disconnectedCallback() {
    this._optionSelectors.forEach((e) =>
      e.removeEventListener("change", this._handleOptionChange)
    );
  }
}
class QuantityInput extends HTMLElement {
  constructor() {
    super(),
      (this.input = this.querySelector("input")),
      (this.changeEvent = new Event("change", { bubbles: !0 })),
      this.querySelectorAll("button").forEach((e) =>
        e.addEventListener("click", this.onButtonClick.bind(this))
      );
  }
  onButtonClick(e) {
    e.preventDefault();
    var t = this.input.value;
    "plus" === e.target.name ? this.input.stepUp() : this.input.stepDown(),
      t !== this.input.value && this.input.dispatchEvent(this.changeEvent);
  }
}
function uniquifyClonedIds(root) {
  if (!root) return;

  if (!window.__mobileCloneUid) window.__mobileCloneUid = 0;
  window.__mobileCloneUid += 1;
  const suffix = '__mqa_' + window.__mobileCloneUid;

  const idMap = new Map();

  // rewrite all ids in the cloned subtree
  root.querySelectorAll('[id]').forEach(el => {
    const oldId = el.id;
    if (!oldId) return;
    const newId = oldId + suffix;
    idMap.set(oldId, newId);
    el.id = newId;
  });

  if (idMap.size === 0) return;

  // fix attributes that reference ids
  const refAttrs = [
    'for',
    'aria-labelledby',
    'aria-describedby',
    'aria-controls',
    'aria-owns'
  ];

  root.querySelectorAll('*').forEach(el => {
    refAttrs.forEach(attr => {
      const val = el.getAttribute(attr);
      if (!val) return;

      const parts = val.split(/\s+/).map(v => idMap.get(v) || v);
      el.setAttribute(attr, parts.join(' '));
    });

    const href = el.getAttribute('href');
    if (href && href.startsWith('#')) {
      const key = href.slice(1);
      if (idMap.has(key)) {
        el.setAttribute('href', '#' + idMap.get(key));
      }
    }
  });

  // keep swatch helpers in sync
  root.querySelectorAll('.product-swatch__input').forEach(input => {
    if (input.dataset.swatchId && idMap.has(input.dataset.swatchId)) {
      input.dataset.swatchId = idMap.get(input.dataset.swatchId);
    }
    if (input.name) {
      input.name = input.name + suffix;
    }
  });

  root.querySelectorAll('[data-swatch-id]').forEach(el => {
    const v = el.dataset.swatchId;
    if (v && idMap.has(v)) {
      el.dataset.swatchId = idMap.get(v);
    }
  });
}

class ProductCard extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    (this._selectors = {
      addToCart: "[js-add-to-cart]",
      loadingSpinner: "loading-spinner",
      price: "[js-prices]",
      swatch: "[js-swatch]",
      variantImage: "[js-variant-image]",
      showMoreTrigger: "[js-collection-show-more-trigger]",
      showMore: "[js-collection-show-more]",
      openMobileQuickAdd: "[js-open-mobile-quickadd]",
      mobileQuickAddContent: "[js-mobile-quickadd-content]",
      productInfo: "[js-product-info]",
      productQuickAdd: "[js-product-quickadd]",
      toggleDesktopQuickAdd: "[js-toogle-desktop-quickadd]",
      closeDesktopQuickAdd: "[js-close-desktop-quickadd]",
      productSwatches: "[js-product-swatches]",
    }),
      (this._elements = {
        addToCart: this.querySelector(this._selectors.addToCart),
        loadingSpinner: this.querySelectorAll(this._selectors.loadingSpinner),
      }),
      (this.openMobileQuickAdd = this.querySelector(
        this._selectors.openMobileQuickAdd
      )),
      (this.mobileQuickAddContent = document.querySelector(
        this._selectors.mobileQuickAddContent
      )),
      (this.productInfo = this.querySelector(this._selectors.productInfo)),
      (this.productQuickAdd = this.querySelector(
        this._selectors.productQuickAdd
      )),
      (this.toggleDesktopQuickAdd = this.querySelector(
        this._selectors.toggleDesktopQuickAdd
      )),
      (this.closeDesktopQuickAdd = this.querySelector(
        this._selectors.closeDesktopQuickAdd
      )),
      (this.quickAddDrawer = document.querySelector("#MobileQuickAddDrawer")),
      this._setListeners(),
      this._initFilterVariantImage(),
      this._initShowMoreSwatches();
  }
  _setListeners() {
    this.openMobileQuickAdd &&
      this.openMobileQuickAdd.addEventListener(
        "click",
        this._renderMobileQuickAddContent
      ),
      this.toggleDesktopQuickAdd &&
      (this.toggleDesktopQuickAdd.addEventListener(
        "click",
        this._toggleDesktopQuickAdd
      ),
        document.addEventListener("click", this.toggleDesktopQuickAdd)),
      this.closeDesktopQuickAdd &&
      this.closeDesktopQuickAdd.addEventListener(
        "click",
        this._toggleDesktopQuickAdd
      );
  }
 _renderMobileQuickAddContent = (e) => {
  e.stopPropagation();
  this._populateDrawerFromCard(this);
  this.quickAddDrawer.open();
};

_populateDrawerFromCard = (card) => {
  this.mobileQuickAddContent.innerHTML = "";

  let t = card.querySelector("[js-product-info]").cloneNode(true);
  t.classList.add("w-1/2");
  t.classList.add("mx-auto");

  let s = card.querySelector("[js-product-quickadd]").cloneNode(true);

  uniquifyClonedIds(t);
  uniquifyClonedIds(s);

  const c1 = s.querySelector(".product-color-swatch__container");
  if (c1) c1.classList.add("justify-start");

  const c2 = s.querySelector(".product-swatch__container");
  if (c2) c2.classList.add("justify-start");

  this.mobileQuickAddContent.append(t, s);

  const sw = this.mobileQuickAddContent.querySelector("[js-product-swatches]");
  if (sw) sw.classList.add("hidden");

  // Bind color swatch handlers for multi-variant products in the drawer
  this.mobileQuickAddContent.querySelectorAll(".pr-swatch-color").forEach((swatch) => {
    swatch.addEventListener("click", (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      const productId = swatch.getAttribute("data-swseid");
      const targetCard = document.querySelector(`product-card[data-prinnid="${productId}"]`);
      if (targetCard) {
        this._populateDrawerFromCard(targetCard);
        this.quickAddDrawer.open();
      }
    });
  });

  variantChecker();
};

  _toggleDesktopQuickAdd = (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (window.innerWidth <= 1200 && this.productQuickAdd && this.quickAddDrawer) {
      this._populateDrawerFromCard(this);
      this.quickAddDrawer.open();
    } else {
      this.classList.toggle("quickadd-desktop-open");
    }
  };
  _closeDesktopQuickAdd = (e) => {
    !this.contains(e.target) &&
      this.classList.contains("quickadd-desktop-open") &&
      (e.preventDefault(), this.classList.remove("quickadd-desktop-open"));
  };
  _initShowMoreSwatches() {
    this.querySelectorAll(this._selectors.showMoreTrigger).forEach((e) => {
      e.addEventListener("click", (e) => {
        this._showMoreSwatches(e);
      });
    });
  }
  _showMoreSwatches(e) {
    const t = e.currentTarget;
    t.classList.add("hidden"),
      this.querySelectorAll(this._selectors.showMore).forEach((e) => {
        e.classList.remove("hidden");
      });
  }
  _initFilterVariantImage() {
    1 < this.querySelectorAll(this._selectors.swatch).length &&
      (this.querySelectorAll(this._selectors.swatch).forEach((e) => {
        e.classList.contains("active") &&
          null != (e = e.dataset.color) &&
          this._filterImage(e);
      }),
        this.querySelectorAll(this._selectors.swatch).forEach((e) => {
          e.addEventListener("click", (e) => {
            e.preventDefault(), this._filterVariantImage(e);
          });
        }));
  }
  _filterVariantImage(e) {
    const t = e.currentTarget;
    e = t.dataset.color;
    this.querySelectorAll(this._selectors.swatch).forEach((e) => {
      e.classList.remove("active");
    }),
      t.classList.add("active"),
      this._filterImage(e);
  }
  _filterImage(s) {
    this.querySelectorAll(this._selectors.variantImage).forEach((e) => {
      const t = e.dataset.alt;
      t.includes(s)
        ? (e.classList.remove("hidden"), e.classList.add("block"))
        : (e.classList.remove("block"), e.classList.add("hidden"));
    });
  }
}
class LoadingSpinner extends HTMLElement {
  constructor() {
    super();
    const e = document.createElement("template");
    (e.innerHTML = `
    <style>
      :host {
        display: none;
        position: absolute;
        top: 0; right: 0; bottom: 0; left: 0;
        width: 100%;
        height: 100%;
      }

      :host([loading]) {
        display: block;
      }

      .background {
        position: absolute;
        top: 0; right: 0; bottom: 0; left: 0;
        width: 100%;
        height: 100%;
        background: var(--background, 'inherit');
        opacity: var(--bg-opacity, 50%);
        z-index: 1;
      }

      .loading-spinner {
        display: flex;
        justify-content: center;
        align-items: center;
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        height: 100%;
        z-index: 2
      }

      .loading-spinner div {
        box-sizing: border-box;
        display: block;
        position: absolute;
        width: var(--size, 5rem);
        height: var(--size, 5rem);
        margin: 0.5rem;
        border: 0.5rem solid var(--loading-color, #fff);
        border-radius: 50%;
        animation: loading-spinner 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
        border-color: var(--loading-color, #fff) transparent transparent transparent;
      }
      .loading-spinner div:nth-child(1) {
        animation-delay: -0.45s;
      }
      .loading-spinner div:nth-child(2) {
        animation-delay: -0.3s;
      }
      .loading-spinner div:nth-child(3) {
        animation-delay: -0.15s;
      }
      @keyframes loading-spinner {
        0% {
          transform: rotate(0deg);
        }
        100% {
          transform: rotate(360deg);
        }
      }
    </style>

    <div class="background"></div>
    <div class="loading-spinner"><div></div><div></div><div></div><div></div></div>
    `),
      this.attachShadow({ mode: "open" }),
      this.shadowRoot.appendChild(e.content.cloneNode(!0));
  }
}

class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    // Elements
    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('[js-predictive-search]');

    // Cache the fallback panel & grid so we can hide/show it when predictive is active
    this.panel = document.getElementById('search-fallback');
    this.fallbackGrid = this.panel ? this.panel.querySelector('.predictive_grid_main') : null;

    // Debounced input handler
    this.input.addEventListener(
      'input',
      theme.utils.debounce((e) => {
        this.onChange(e);
      }, 300).bind(this)
    );

    // Fallback search logic when the search input is empty
    // Prevent multiple DOMContentLoaded listeners
    if (window.rsvltsSearchInitialized) return;
    window.rsvltsSearchInitialized = true;

    document.addEventListener('DOMContentLoaded', function () {
      try {
        const input = document.querySelector('#desktop-search');
        const inputMobile = document.querySelector('.mobile-search-input');
        const fallback = document.getElementById('search-fallback');
        const recentList = document.getElementById('search-fallback__recent');
        const trendingList = document.getElementById('search-fallback__trending');

        // Early exit if required elements don't exist
        if (!fallback || !recentList || !trendingList) return;

        const RECENT_LIMIT = 5;
        const STORAGE_KEY = 'rsvlts_recent_searches';
        const EXPIRY_DAYS = 30;

        function getStoredSearches() {
          try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (!stored) return [];
            const parsed = JSON.parse(stored);
            if (!Array.isArray(parsed)) return [];
            const now = new Date().getTime();
            return parsed.filter((entry) => entry && entry.timestamp && now - entry.timestamp < EXPIRY_DAYS * 86400000);
          } catch (error) {
            console.warn('Error reading stored searches:', error);
            return [];
          }
        }

        function storeSearch(term) {
          try {
            if (!term || !term.trim()) return;
            const now = new Date().getTime();
            let searches = getStoredSearches();
            searches = searches.filter((s) => s.term.toLowerCase() !== term.toLowerCase());
            searches.unshift({ term: term.trim(), timestamp: now });
            if (searches.length > RECENT_LIMIT) searches = searches.slice(0, RECENT_LIMIT);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
          } catch (error) {
            console.warn('Error storing search:', error);
          }
        }

        // Debounced render function to prevent excessive DOM manipulation
        let renderTimeout;
        function renderRecentSearches() {
          clearTimeout(renderTimeout);
          renderTimeout = setTimeout(() => {
            try {
              const recent = getStoredSearches();
              const label = document.querySelector('[data-label="recent-searches"]');

              if (!label || !recentList || !trendingList) return;

              if (recent.length > 0) {
                // Show Recent
                recentList.classList.remove('hidden');
                recentList.classList.add('min-h-[101px]');
                trendingList.classList.add('hidden');
                trendingList.classList.remove('min-h-[101px]');
                label.textContent = label.dataset.labelRecent || 'Recent Searches';

                recentList.innerHTML = recent
                  .map(
                    (s) =>
                      `<li><a href="/search?q=${encodeURIComponent(
                        s.term
                      )}" class="text-blue-dark hover:text-blue">${s.term}</a></li>`
                  )
                  .join('');
              } else {
                // Show Trending
                recentList.classList.add('hidden');
                recentList.classList.remove('min-h-[101px]');
                trendingList.classList.remove('hidden');
                trendingList.classList.add('min-h-[101px]');
                label.textContent = label.dataset.labelFallback || 'Trending';
              }
            } catch (error) {
              console.warn('Error rendering recent searches:', error);
            }
          }, 100);
        }

        // Initialize label
        try {
          const labelElement = document.querySelector('[data-label="recent-searches"]');
          if (labelElement) {
            const recentSearches = getStoredSearches();
            labelElement.textContent =
              recentSearches.length > 0 ? (labelElement.dataset.labelRecent || 'Recent Searches') : (labelElement.dataset.labelFallback || 'Trending');
          }
        } catch (error) {
          console.warn('Error initializing label:', error);
        }

        // Initial render
        renderRecentSearches();

        // Single consolidated mousedown handler to prevent memory leaks
        let globalMouseHandler;
        function setupGlobalMouseHandler() {
          if (globalMouseHandler) return; // Prevent duplicate handlers

          globalMouseHandler = (e) => {
            try {
              if (!fallback || fallback.classList.contains('hidden')) return;

              const isDesktopInput = input && e.target === input;
              const isMobileInput = inputMobile && e.target === inputMobile;
              const isInsideFallback = fallback.contains(e.target);

              if (!isDesktopInput && !isMobileInput && !isInsideFallback) {
                fallback.classList.add('hidden');
              }
            } catch (error) {
              console.warn('Error in global mouse handler:', error);
            }
          };

          document.addEventListener('mousedown', globalMouseHandler, { passive: true });
        }

        // Desktop input handlers
        if (input && fallback) {
          input.addEventListener('focus', () => {
            try {
              fallback.classList.remove('hidden');
              renderRecentSearches();
              setupGlobalMouseHandler();
            } catch (error) {
              console.warn('Error on desktop focus:', error);
            }
          });

          input.addEventListener('input', () => {
            try {
              if (input.value.trim() === '') {
                fallback.classList.remove('hidden');
                renderRecentSearches();
              }
            } catch (error) {
              console.warn('Error on desktop input:', error);
            }
          });

          if (input.form) {
            input.form.addEventListener('submit', () => {
              try {
                storeSearch(input.value.trim());
              } catch (error) {
                console.warn('Error on desktop submit:', error);
              }
            });
          }
        }

        // Mobile input handlers
        if (inputMobile && fallback) {
          inputMobile.addEventListener('focus', () => {
            try {
              fallback.classList.remove('hidden');
              renderRecentSearches();
              setupGlobalMouseHandler();
            } catch (error) {
              console.warn('Error on mobile focus:', error);
            }
          });

          inputMobile.addEventListener('input', () => {
            try {
              if (inputMobile.value.trim().length > 0) {
                fallback.classList.add('hidden');
              } else if (inputMobile.value.trim() === '') {
                fallback.classList.remove('hidden');
                renderRecentSearches();
              }
            } catch (error) {
              console.warn('Error on mobile input:', error);
            }
          });

          if (inputMobile.form) {
            inputMobile.form.addEventListener('submit', () => {
              try {
                storeSearch(inputMobile.value.trim());
              } catch (error) {
                console.warn('Error on mobile submit:', error);
              }
            });
          }
        }

        // Save search term when clicking product cards in search results
        function setupProductClickHandlers() {
          const fallbackContainer = document.getElementById('search-fallback');

          function handleProductClick(e) {
            // Find if click is on or within a product card link
            const productLink = e.target.closest('product-card a, .mobile_search_result a, .predictive-search__item');
            if (!productLink) return;

            // Get current search term from the appropriate input
            const isMobileView = window.innerWidth < 1025;
            const currentInput = isMobileView ? inputMobile : input;
            const searchTerm = currentInput?.value?.trim();

            // Only save if there's a search term
            if (searchTerm && searchTerm.length > 0) {
              storeSearch(searchTerm);
            }
          }

          // Add listener to fallback container
          if (fallbackContainer) {
            fallbackContainer.addEventListener('click', handleProductClick);
          }

          // Use event delegation on document for predictive search results
          // since they are dynamically loaded
          document.addEventListener('click', (e) => {
            const predictiveContainer = document.getElementById('predictive-search-results');
            if (predictiveContainer && predictiveContainer.contains(e.target)) {
              handleProductClick(e);
            }
          });
        }

        // Initialize product click handlers
        setupProductClickHandlers();

        // Search panel controls
        const openSearch = document.getElementById('open-search-panel');
        const closeSearch = document.getElementById('close-search-panel');
        const panel = document.getElementById('search-fallback');

        function focusAtEnd(el) {
          try {
            if (!el) return;
            el.focus();
            const len = el.value.length;
            el.setSelectionRange(len, len);
            el.value = el.value;
          } catch (error) {
            console.warn('Error focusing element:', error);
          }
        }

        if (openSearch && panel) {
          openSearch.addEventListener('click', () => {
            try {
              panel.classList.remove('hidden');
              const searchInput = panel.querySelector('input[type="search"]');
              if (searchInput) focusAtEnd(searchInput);
            } catch (error) {
              console.warn('Error opening search panel:', error);
            }
          });
        }

        if (closeSearch && panel) {
          closeSearch.addEventListener('click', () => {
            try {
              panel.classList.add('hidden');
            } catch (error) {
              console.warn('Error closing search panel:', error);
            }
          });
        }

        // Focus handler for panel search input
        if (panel) {
          const searchInput = panel.querySelector('input[type="search"]');
          if (searchInput) {
            searchInput.addEventListener('focus', () => {
              try {
                focusAtEnd(searchInput);
              } catch (error) {
                console.warn('Error on search input focus:', error);
              }
            });
          }
        }

      } catch (error) {
        console.error('Error initializing search functionality:', error);
      }
    }); // End fallback logic
  }

  /* ---------- helpers to control the fallback grid ---------- */
  hideFallbackGrid() {
    if (this.fallbackGrid) this.fallbackGrid.classList.add('hidden');
  }
  showFallbackGrid() {
    if (this.fallbackGrid) this.fallbackGrid.classList.remove('hidden');
  }

  /* ---------- input change ---------- */
  onChange() {
    const q = this.input.value.trim();

    if (!q.length) {
      // empty → close predictive and show fallback
      this.close();
      this.showFallbackGrid();
      this.searchDrawerHeight();
      return;
    }

    // user typed → show predictive immediately (before fetch) and hide fallback
    this.hideFallbackGrid();
    this.open();
    this.searchDrawerHeight();
    this.getSearchResults(q);
  }

  /* ---------- fetch predictive results ---------- */
  getSearchResults(q) {
    fetch(
      routes.predictive_search_url +
      `?q=${q}&resources[type]=product,collection,page&resources[limit]=5&section_id=predictive-search&resources[limit_scope]=each&resources[options][fields]=body,title`
    )
      .then((r) => {
        if (r.ok) return r.text();
        throw new Error(r.status);
      })
      .then((html) => {
        const inner = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;

        this.predictiveSearchResults.innerHTML = inner;
        this.open();             // ensure predictive stays open
        this.hideFallbackGrid(); // belt & suspenders
        this.searchDrawerHeight();
      })
      .catch((err) => {
        // graceful fallback on error
        this.close();
        this.showFallbackGrid();
        this.searchDrawerHeight();
        console.error('Predictive search error:', err);
      });
  }

  /* ---------- toggle predictive container ---------- */
  open() {
    this.predictiveSearchResults.classList.remove('hidden');
    this.predictiveSearchResults.classList.add('block');
    this.hideFallbackGrid();
  }

  close() {
    this.predictiveSearchResults.classList.remove('block');
    this.predictiveSearchResults.classList.add('hidden');
    if (!this.input.value.trim()) this.showFallbackGrid();
  }

  /* ---------- adjust drawer height ---------- */
  searchDrawerHeight() {
    const drawer = document.querySelector('#SearchDrawer');
    const headerH = document.querySelector('#shopify-section-header').offsetHeight;
    const h = this.predictiveSearchResults.classList.contains('hidden') ? headerH + 'px' : 'max-content';
    drawer.style.height = h;
  }
}

// class PredictiveSearch extends HTMLElement {
//   constructor() {
//     super(),
//       (this.input = this.querySelector('input[type="search"]')),
//       (this.predictiveSearchResults = this.querySelector(
//         "[js-predictive-search]"
//       )),
//       this.input.addEventListener(
//         "input",
//         theme.utils
//           .debounce((e) => {
//             this.onChange(e);
//           }, 300)
//           .bind(this)
//       );

//     // Fallback search logic when the search input is empty
//     document.addEventListener("DOMContentLoaded", function () {
//       const input = document.querySelector('input[type="search"]');
//       const inputMobile = document.querySelector('.mobile-search-input');
//       const fallback = document.getElementById("search-fallback");
//       const recentList = document.getElementById("search-fallback__recent");
//       const trendingList = document.getElementById("search-fallback__trending");

//       const RECENT_LIMIT = 5;
//       const STORAGE_KEY = "rsvlts_recent_searches";
//       const EXPIRY_DAYS = 30;

//       function getStoredSearches() {
//         try {
//           const stored = JSON.parse(localStorage.getItem(STORAGE_KEY));
//           if (!stored) return [];
//           const now = new Date().getTime();
//           return stored.filter(entry => now - entry.timestamp < EXPIRY_DAYS * 86400000);
//         } catch {
//           return [];
//         }
//       }

//       function storeSearch(term) {
//         if (!term.trim()) return;
//         const now = new Date().getTime();
//         let searches = getStoredSearches();
//         searches = searches.filter(s => s.term.toLowerCase() !== term.toLowerCase());
//         searches.unshift({ term, timestamp: now });
//         if (searches.length > RECENT_LIMIT) searches = searches.slice(0, RECENT_LIMIT);
//         localStorage.setItem(STORAGE_KEY, JSON.stringify(searches));
//       }

//       function renderRecentSearches() {
//         const recent = getStoredSearches();
//         const label = document.querySelector('[data-label="recent-searches"]');
//         const recentList = document.getElementById('search-fallback__recent');
//         const trendingList = document.getElementById('search-fallback__trending');

//         if (recent.length > 0) {
//           // Show Recent
//           recentList.classList.remove('hidden');
//           recentList.classList.add('min-h-[101px]');
//           trendingList.classList.add('hidden');
//           trendingList.classList.remove('min-h-[101px]');
//           label.textContent = label.dataset.labelRecent;

//           recentList.innerHTML = recent
//             .map(s => `<li><a href=\"/search?q=${encodeURIComponent(s.term)}\" class=\"text-blue-dark hover:text-blue\">${s.term}</a></li>`)
//             .join('');
//         } else {
//           // Show Trending
//           recentList.classList.add('hidden');
//           recentList.classList.remove('min-h-[101px]');
//           trendingList.classList.remove('hidden');
//           trendingList.classList.add('min-h-[101px]');
//           label.textContent = label.dataset.labelFallback;
//         }
//       }

//       const labelElement = document.querySelector('[data-label="recent-searches"]');
//       const recentSearches = getStoredSearches();

//       if (recentSearches.length > 0) {
//         labelElement.textContent = labelElement.dataset.labelRecent;
//       } else {
//         labelElement.textContent = labelElement.dataset.labelFallback;
//       }

//       // Ensure the correct list is visible on load (without waiting for input focus)
//       renderRecentSearches();

//       // Input desktop
//       if (input && fallback) {
//         input.addEventListener("focus", () => {
//           // if (!input.value.trim()) {
//             fallback.classList.remove("hidden");
//             renderRecentSearches();
//           // }
//         });

//         input.addEventListener('input', () => {
//           if (input.value.trim() === '') {
//             fallback.classList.remove('hidden');
//             renderRecentSearches();
//           }
//         });

//         document.addEventListener("mousedown", (e) => {
//           if (!fallback.contains(e.target) && e.target !== input) {
//             fallback.classList.add("hidden");
//           }
//         });

//          input.form?.addEventListener("submit", () => {
//           storeSearch(input.value.trim());
//         });
//       }

//       // Input mobile
//       if (inputMobile && fallback) {
//         inputMobile.addEventListener("focus", () => {
//           // if (!input.value.trim()) {
//             fallback.classList.remove("hidden");
//             renderRecentSearches();
//           // }
//         });

//         inputMobile.addEventListener('input', () => {
//           if (inputMobile.value.trim().length > 0) {
//             fallback.classList.add('hidden');
//           }
//           else if (inputMobile.value.trim() === '') {
//             fallback.classList.remove('hidden');
//             renderRecentSearches();
//           }

//         });

//         document.addEventListener("mousedown", (e) => {
//           if (!fallback.contains(e.target) && e.target !== inputMobile) {
//             fallback.classList.add("hidden");
//           }
//         });

//          inputMobile.form?.addEventListener("submit", () => {
//           storeSearch(inputMobile.value.trim());
//         });
//       }

//       const openSearch = document.getElementById('open-search-panel');
//       const closeSearch = document.getElementById('close-search-panel');
//       const panel      = document.getElementById('search-fallback');
//       const searchInput  = panel.querySelector('input[type="search"]');

//       openSearch.addEventListener('click', () => {
//         panel.classList.remove('hidden');
//         panel.querySelector('input[type="search"]').focus();
//       });

//       // Utility to focus & move cursor to end
//       function focusAtEnd(input) {
//         input.focus();
//         const len = input.value.length;
//         // For modern browsers
//         input.setSelectionRange(len, len);
//         // Fallback for older browsers
//         input.value = input.value;
//       }

//       openSearch.addEventListener('click', () => {
//         panel.classList.remove('hidden');
//         focusAtEnd(searchInput);
//       });

//       searchInput.addEventListener('focus', () => {
//         // Focus via tab or other means, also moves caret
//         focusAtEnd(searchInput);
//       });

//       closeSearch.addEventListener('click', () => {
//         panel.classList.add('hidden');
//       });
//     });  // End of fallback search logic
//   }
//   onChange() {
//     var e = this.input.value.trim();
//     if (!e.length) return this.close(), void this.searchDrawerHeight();
//     this.getSearchResults(e);
//   }
//         //`?q=${e}&resources[type]=product,collection,page&resources[limit]=5&section_id=predictive-search&resources[limit_scope]=each`

//   getSearchResults(e) {
//     fetch(
//       routes.predictive_search_url +
//         `?q=${e}&resources[type]=product,collection,page&resources[limit]=5&section_id=predictive-search&resources[limit_scope]=each&resources[options][fields]=body,title`

//     )
//       .then((e) => {
//         if (e.ok) return e.text();
//         throw (
//           ((e = new Error(e.status)),
//           this.close(),
//           this.searchDrawerHeight(),
//           e)
//         );
//       })
//       .then((e) => {
//         e = new DOMParser()
//           .parseFromString(e, "text/html")
//           .querySelector("#shopify-section-predictive-search").innerHTML;
//         (this.predictiveSearchResults.innerHTML = e), this.open();
//         this.searchDrawerHeight();
//       })
//       .catch((e) => {
//         throw (this.close(), this.searchDrawerHeight(), e);
//       });
//   }
//   open() {
//     this.predictiveSearchResults.classList.remove("hidden"),
//       this.predictiveSearchResults.classList.add("block");
//   }
//   close() {
//     this.predictiveSearchResults.classList.remove("block"),
//       this.predictiveSearchResults.classList.add("hidden");
//   }
//   searchDrawerHeight() {
//     const e = document.querySelector("#SearchDrawer");
//     var t = document.querySelector("#shopify-section-header").offsetHeight;
//     let s;
//     (s = this.predictiveSearchResults.classList.contains("hidden")
//       ? t + "px"
//       : "max-content"),
//       (e.style.height = s);
//   }
// }
class ScrollToTop extends HTMLElement {
  constructor() {
    super(),
      document.addEventListener("scroll", this._handleScroll),
      this.querySelector("[js-scroll-top]").addEventListener(
        "click",
        this._scrollToTop
      );
  }
  _handleScroll = () => {
    100 < document.body.scrollTop || 100 < document.documentElement.scrollTop
      ? this.querySelector("[js-scroll-top]").classList.add("btn-show")
      : this.querySelector("[js-scroll-top]").classList.remove("btn-show");
  };
  _scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
}
class ProductBundle extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        productBundle: "[js-product-bundle]",
        productTitle: "[js-product-title]",
        bundleATC: "[js-product-bundle-atc]",
        mainProductATC: "[js-add-to-cart]",
        bundleSwatch: "[js-product-bundle-swatch]",
        outfitSwatch: "[js-product-outfit-swatch]",
        bundleDiscount: "[js-product-bundle-discount]",
      });
  }
  connectedCallback() {
    this._setListeners();
  }
  _setListeners() {
    this.querySelectorAll(this._selectors.bundleSwatch).forEach((e) => {
      e.addEventListener("click", this._bundleSwatchSelection.bind(this));
    }),
      this.querySelectorAll(this._selectors.outfitSwatch).forEach((e) => {
        e.addEventListener("click", this._outfitATC.bind(this));
      });
  }
  _bundleSwatchSelection = (e) => {
    e.preventDefault();
    let t = e.target;
    t.classList.add("selected"),
      this.querySelectorAll(this._selectors.bundleSwatch).forEach((e) => {
        e !== t && e.classList.remove("selected");
      });
  };
  _outfitATC = (e) => {
    const t = document.querySelectorAll(this._selectors.mainProductATC);
    document.querySelectorAll(this._selectors.productBundle).length ==
      document.querySelectorAll(this._selectors.outfitSwatch + ".selected")
        .length &&
      t.forEach((e) => {
        e.removeAttribute("disabled"),
          e.classList.add("btn--black"),
          (e.innerText = "Add To Cart"),
          e.addEventListener("click", this._addMatchingSet.bind(this));
      });
  };
  _addMatchingSet = (e) => {
    e.preventDefault();
    let t = document.querySelectorAll(
      this._selectors.outfitSwatch + ".selected"
    ),
      s = [];
    for (let e = 0; e < t.length; e++) {
      var r = t[e].closest(this._selectors.bundleDiscount).dataset.discount;
      s.push({
        id: t[e].dataset.id,
        quantity: 1,
        properties: {
          _matchingSet: Math.ceil(r),
          _matchingSetCount: t.length,
          _matchingSetParent: document.querySelector(
            this._selectors.productTitle
          ).dataset.handle,
        },
      });
    }
    e = { items: s.reverse() };
    theme.CartAPI.add(e);
  };
}
class ShowPassword extends HTMLElement {
  constructor() {
    super(),
      this.querySelector("[js-show-password]").addEventListener(
        "click",
        this._togglePassword
      );
  }
  _togglePassword = () => {
    let e = this.querySelector("[js-password-input]");
    "password" == e.type ? (e.type = "text") : (e.type = "password");
  };
}
class AccountFlow extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        accountDropdownTrigger: "[js-account-dropdown-trigger]",
        accountDropdown: "[js-account-dropdown]",
        accountDrawerTrigger: "[js-account-drawer-trigger]",
        accountDrawer: "[js-account-drawer]",
        loginDesktopBtn: "[js-login-desktop]",
        registerDesktopBtn: "[js-register-desktop]",
      }),
      (this.elements = this._getElements()),
      this._setupEventListeners();
  }
  _getElements() {
    return {
      accountDropdownTrigger: this.querySelector(
        this._selectors.accountDropdownTrigger
      ),
      accountDropdown: this.querySelector(this._selectors.accountDropdown),
      accountDrawerTrigger: this.querySelector(
        this._selectors.accountDrawerTrigger
      ),
      accountDrawer: this.querySelector(this._selectors.accountDrawer),
      loginDesktopButtons: this.querySelectorAll(
        this._selectors.loginDesktopBtn
      ),
      registerDesktopButtons: this.querySelectorAll(
        this._selectors.registerDesktopBtn
      ),
    };
  }
  _setupEventListeners() {
    this.elements.accountDrawerTrigger &&
      this.elements.accountDrawerTrigger.addEventListener(
        "click",
        this._openAccountDrawer
      ),
      // this.elements.accountDrawer.addEventListener(
      //   "mouseleave",
      //   this._closeAccountDrawer
      // ),
      this.elements.loginDesktopButtons.forEach((e) => {
        e.addEventListener("click", this._handleLoginButtonClick);
      }),
      this.elements.registerDesktopButtons.forEach((e) => {
        e.addEventListener("click", this._handleRegisterButtonClick);
      });
  }
  _openAccountDrawer = () => {
    document.querySelector("#AccountDrawer").open();
  };
  _closeAccountDrawer = () => {
    document.querySelector("#AccountDrawer").close();
  };
  _handleLoginButtonClick = () => {
    document.querySelector("#LoginModal").open();
  };
  _handleRegisterButtonClick = () => {
    document.querySelector("#RegisterModal").open();
  };
}
class QuickAdd extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = { addToCart: "[js-add-to-cart]" }),
      (this._isAdding = false);
  }
  connectedCallback() {
    (this._elements = {
      addToCart: this.querySelector(this._selectors.addToCart),
    }),
      this._setListeners(),
      this._initAddToCart();
  }
  _setListeners() {
    this.addEventListener("variant:change", this._handleVariantChange);
    this.addEventListener("change", this._handleSwatchChange);
  }
  _initAddToCart() {
    const e = this._elements["addToCart"];
    e && e.addEventListener("click", this._addToCart);
  }
  // Resolve locally the instant a swatch changes, so data-variant-id and the button
  // label are correct without waiting for /products/{handle}.js to come back.
  _handleSwatchChange = (t) => {
    if (!t.target || "function" != typeof t.target.matches) return;
    if (!t.target.matches("[js-product-swatch-input]")) return;
    if (!this.dataset.variants) return; // nothing to resolve from; leave the async path alone
    this._updateAddToCartState(resolveQuickAddVariant(this));
  };
  _addToCart = async (t) => {
    t?.preventDefault();
    if (this._isAdding) return; // a fast double-click would otherwise add two items

    const button = this._elements?.addToCart;
    if (!button) return;

    // Re-resolve from the live DOM at click time. data-variant-id is written
    // asynchronously, so it can still hold the liquid-rendered first available
    // variant when a shopper picks a size and clicks straight away.
    let id;
    if (this.dataset.variants && this.querySelector("[js-product-swatch-input]")) {
      const variant = resolveQuickAddVariant(this);
      if (!variant) {
        this._updateAddToCartState(null); // -> "Select Options", disabled
        return;
      }
      this._updateAddToCartState(variant);
      if (!variant.available) return;
      id = variant.id;
    } else {
      id = button.dataset.variantId; // single-variant card / no embedded variants JSON
    }

    if (!id || "NULL" == id) return;

    this._isAdding = true;
    try {
      await theme.CartAPI.add({ items: [{ id: id, quantity: 1, properties: {} }] });
    } catch (e) {
      console.error(e);
    } finally {
      this._isAdding = false;
    }
  };
  _handleVariantChange = (t) => {
    t.stopPropagation();
    // Prefer our own embedded variants JSON over the async ProductOptions result,
    // so a late-arriving event can't overwrite an already-correct variant id.
    const local = this.dataset.variants ? resolveQuickAddVariant(this) : null;
    const e = local || t.detail.variant;
    (this.colorSpan = this.querySelector(".product-swatch-colors__selected")),
      this.colorSpan && e && (this.colorSpan.innerHTML = e.option2),
      this._updateAddToCartState(e);
  };
  _updateAddToCartState(e) {
    const t = this.querySelector(this._selectors.addToCart);
    if (!t) return;
    e
      ? ((t.dataset.variantId = e.id),
        e.available
          ? ((t.textContent = "Add To Cart"),
            t.removeAttribute("disabled"),
            t.classList.remove("cursor-not-allowed"),
            t.classList.remove("sold-out"))
          : ((t.textContent = "Sold out"),
            t.setAttribute("disabled", ""),
            t.classList.add("cursor-not-allowed"),
            t.classList.add("sold-out")))
      : ((t.textContent = "Select Options"),
        t.setAttribute("disabled", ""),
        t.classList.add("cursor-not-allowed"),
        t.classList.add("sold-out"));
  }
  _removeEventListeners() {
    this.removeEventListener("variant:change", this._handleVariantChange);
    this.removeEventListener("change", this._handleSwatchChange);
  }
  disconnectedCallback() {
    this._removeEventListeners();
  }
}
class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    // Processing state to prevent concurrent submissions
    this._isProcessing = false;
    this._abortController = null;
    this._debounceTimer = null;
    this._REQUEST_TIMEOUT = 8000; // 8 second timeout

    this.elements = {
      input: this.querySelector('input[name="language_code"], input[name="country_code"]'),
      button: this.querySelector("button:not([js-close])"),
      close: this.querySelector("[js-close]"),
      panel: this.querySelector("ul"),
      form: this.querySelector("form"),
    };

    // Bind event handlers
    if (this.elements.button) {
      this.elements.button.addEventListener("click", this.openSelector.bind(this));
    }
    if (this.elements.close) {
      this.elements.close.addEventListener("click", this.hidePanel.bind(this));
    }

    // Attach click handlers to all country links
    this.querySelectorAll("a[data-value]").forEach((link) => {
      link.addEventListener("click", this._onItemClickDebounced.bind(this));
    });
  }

  // Debounced click handler to prevent rapid clicks
  _onItemClickDebounced(e) {
    e.preventDefault();
    e.stopPropagation();

    // Clear any existing debounce timer
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }

    // Block if already processing
    if (this._isProcessing) {
      return;
    }

    // Debounce: wait 50ms before processing (fast but prevents accidental double-clicks)
    const target = e.currentTarget;
    this._debounceTimer = setTimeout(() => {
      this._onItemClick(target);
    }, 50);
  }

  // Set loading state on the component
  _setLoadingState(isLoading) {
    this._isProcessing = isLoading;
    if (isLoading) {
      this.classList.add('localization-form--loading');
    } else {
      this.classList.remove('localization-form--loading');
    }
  }

  // Show error message to user
  _showError(message) {
    // Reset loading state
    this._setLoadingState(false);

    // Log for debugging
    console.error('LocalizationForm Error:', message);

    // Show user-friendly error (create temporary error element)
    const errorEl = document.createElement('div');
    errorEl.className = 'localization-form__error';
    errorEl.setAttribute('role', 'alert');
    errorEl.textContent = 'Unable to switch country. Please try again.';
    errorEl.style.cssText = 'color: #c00; padding: 8px; text-align: center; font-size: 12px;';

    // Insert at top of panel
    if (this.elements.panel) {
      const existingError = this.elements.panel.querySelector('.localization-form__error');
      if (existingError) existingError.remove();
      this.elements.panel.insertBefore(errorEl, this.elements.panel.firstChild);

      // Auto-remove after 5 seconds
      setTimeout(() => {
        if (errorEl.parentNode) errorEl.remove();
      }, 5000);
    }
  }

  hidePanel() {
    if (this.elements.button) {
      this.elements.button.style.display = "";
      this.elements.button.setAttribute("aria-expanded", "false");
    }
    if (this.elements.panel) {
      this.elements.panel.setAttribute("hidden", true);
    }
  }

  onContainerKeyUp(e) {
    if (e.code && e.code.toUpperCase() === "ESCAPE") {
      this.hidePanel();
      if (this.elements.button) this.elements.button.focus();
    }
  }

  _onItemClick(target) {
    // Double-check processing state
    if (this._isProcessing) {
      return;
    }

    const countryCode = target.dataset.value;
    if (!countryCode) {
      console.warn('LocalizationForm: No country code found on clicked element');
      return;
    }

    // Set loading state immediately
    this._setLoadingState(true);

    // Hide the panel immediately for better UX
    this.hidePanel();

    // Cancel any pending request
    if (this._abortController) {
      this._abortController.abort();
    }

    // Find the actual localization form (may be nested)
    let localizationForm = this.querySelector("form[action*='/localization']");
    if (!localizationForm) {
      // Fallback: look for form with localization input
      localizationForm = this.elements.form;
    }

    // Check if we have a valid localization form that's not inside a javascript: wrapper
    const hasValidForm = localizationForm &&
                         localizationForm.action &&
                         !localizationForm.action.includes("javascript:") &&
                         localizationForm.action.includes("/localization");

    if (hasValidForm && this.elements.input) {
      // Use native form submission (most reliable)
      this.elements.input.value = countryCode;
      localizationForm.submit();
      // Note: page will reload, no need to reset state
    } else {
      // Use Fetch API for AJAX submission with proper controls
      this._submitViaFetch(countryCode);
    }
  }

  _submitViaFetch(countryCode) {
    // Create new AbortController for this request
    this._abortController = new AbortController();
    const signal = this._abortController.signal;

    // Build form data
    const formData = new FormData();
    formData.append('form_type', 'localization');
    formData.append('country_code', countryCode);
    formData.append('_method', 'put');
    formData.append('return_to', window.location.pathname + window.location.search);

    // Create timeout promise
    const timeoutId = setTimeout(() => {
      if (this._abortController) {
        this._abortController.abort();
      }
    }, this._REQUEST_TIMEOUT);

    fetch('/localization', {
      method: 'POST',
      body: formData,
      signal: signal,
      redirect: 'follow',
      credentials: 'same-origin'
    })
      .then(response => {
        clearTimeout(timeoutId);

        if (!response.ok && !response.redirected) {
          throw new Error(`Server responded with status ${response.status}`);
        }

        // Shopify returns a redirect, follow it
        if (response.redirected) {
          window.location.href = response.url;
        } else {
          // Fallback: reload the page
          window.location.reload();
        }
      })
      .catch(error => {
        clearTimeout(timeoutId);

        // Don't show error for intentional aborts
        if (error.name === 'AbortError') {
          return;
        }

        this._showError(error.message || 'Network error occurred');
      });
  }

  openSelector() {
    if (this.elements.button) {
      this.elements.button.focus();
    }
    if (this.elements.panel) {
      this.elements.panel.toggleAttribute("hidden");
    }
    if (this.elements.button) {
      this.elements.button.setAttribute(
        "aria-expanded",
        (this.elements.button.getAttribute("aria-expanded") === "false").toString()
      );
      this.elements.button.style.display = "none";
    }
  }

  closeSelector(e) {
    const isButtonRelated = e.relatedTarget && e.relatedTarget.nodeName === "BUTTON";
    if (e.relatedTarget === null || isButtonRelated) {
      this.hidePanel();
    }
  }

  // Cleanup on disconnect
  disconnectedCallback() {
    if (this._abortController) {
      this._abortController.abort();
    }
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
  }
}
customElements.define("s-drawer", Drawer),
  customElements.define("s-modal", Modal),
  customElements.define("s-accordion", Accordion),
  customElements.define("s-tabs", Tabs),
  customElements.define("s-ajaxcart", AjaxCart),
  customElements.define("s-carousel", Carousel),
  customElements.define("product-options", ProductOptions),
  customElements.define("quantity-input", QuantityInput),
  customElements.define("product-card", ProductCard),
  customElements.define("loading-spinner", LoadingSpinner),
  customElements.define("predictive-search", PredictiveSearch),
  customElements.define("scroll-to-top", ScrollToTop),
  customElements.define("product-bundle", ProductBundle),
  customElements.define("quick-add", QuickAdd),
  customElements.define("localization-form", LocalizationForm),
  customElements.define("show-password", ShowPassword),
  customElements.define("account-flow", AccountFlow);
class MainCollection extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        productGrid: "[js-products-grid]",
        productGridTrigger: "[js-products-grid-trigger]",
        productEditorial: "[js-products-editorial]",
        productFeaturedCollection: "[js-products-featured-collection]",
        productPromo: "[js-products-promo]",
        pagination: "[js-pagination]",
      }),
      (this._events = { fetch: "section:fetched" }),
      (this._cache = []);
  }
  connectedCallback() {
    this._setListeners();
  }
  _setListeners() {
    this.addEventListener("filter:change", this._handleFilterChange),
      this.querySelectorAll(this._selectors.productGridTrigger).forEach((e) => {
        e.addEventListener("click", (e) => {
          e.preventDefault(), this._switchGrid(e);
        });
      });

    const sortBtn = this.querySelector('[js-sort-toggle]');
    const sortPop = this.querySelector('[js-sort-popover]');
    const filterBtn = this.querySelector('[js-filter-drawer-open]');

    if (sortBtn && sortPop) {
      sortBtn.addEventListener('click', (e) => {
        e.preventDefault();
        sortPop.classList.toggle('hidden');
      });

      if (filterBtn) {
        filterBtn.addEventListener('click', () => {
          sortPop.classList.add('hidden');
        });
      }

      document.addEventListener("click", (e) => {
        if (
          sortPop.classList.contains("hidden") ||
          e.target === sortPop ||
          sortPop.contains(e.target) ||
          e.target === sortBtn ||
          sortBtn.contains(e.target)
        ) {
          // click was on the pop-over itself or on the button, or
          // the pop-over is already hidden → do nothing
          return;
        }
        // otherwise: hide it
        sortPop.classList.add("hidden");
      });

      document.addEventListener("change", (e) => {
        if (
          e.target.matches('input[name="sort_by"]') &&
          !sortPop.classList.contains("hidden")
        ) {
          sortPop.classList.add("hidden");
        }
      });
    }
  }
  _handleFilterChange(e) {
    e = e.detail.searchParams;
    const t = `${window.location.pathname}?section_id=${this.dataset.id}&` + e;
    e = (e) => e.url === t;
    this._cache.some(e) ? this._renderFromCache(e) : this._renderFromFetch(t);
  }
  _switchGrid(e) {
    const t = e.currentTarget,
      s = this.querySelector(this._selectors.productGrid),
      r = this.querySelector(this._selectors.productEditorial),
      i = this.querySelector(this._selectors.productFeaturedCollection),
      o = this.querySelector(this._selectors.productPromo);
    this.querySelectorAll(this._selectors.productGridTrigger).forEach((e) => {
      e.classList.remove("active");
    }),
      t.classList.add("active"),
      s?.classList.remove("grid--small"),
      s?.classList.remove("grid--large"),
      s?.classList.remove("grid--extra-large");
    s?.classList.add(t.dataset.gridSize),
      r?.classList.remove("grid--small"),
      r?.classList.remove("grid--large"),
      r?.classList.remove("grid--extra-large");
    r?.classList.add(t.dataset.gridSize),
      i?.classList.remove("grid--small"),
      i?.classList.remove("grid--large"),
      i?.classList.remove("grid--extra-large");
    i?.classList.add(t.dataset.gridSize),
      o?.classList.remove("grid--small"),
      o?.classList.remove("grid--large"),
      o?.classList.remove("grid--extra-large");
    o?.classList.add(t.dataset.gridSize);
  }
  _renderFromCache(e) {
    var e = this._cache.find(e).html,
      t = new DOMParser().parseFromString(e, "text/html");
    this.querySelector("filter-sort").dispatchEvent(
      new CustomEvent(this._events.fetch, { detail: { html: e } })
    ),
      this._renderProducts(t);
  }
  _renderFromFetch(s) {
    let r = this._cache;
    fetch(s)
      .then((e) => e.text())
      .then((e) => {
        var t = new DOMParser().parseFromString(e, "text/html");
        // Get the count value from the span
      const countSpan = t.querySelector("[data-search-results-count]");
      const countValue = countSpan ? countSpan.textContent.trim() : "0";

      // Extract search term from URL (example: ?q=disney)
      const urlObj = new URL(s, window.location.origin);
      const searchTerm = urlObj.searchParams.get("q") || "";

      // Update the paragraph text dynamically
      const searchCountEl = document.querySelector(".search-count");

      if (searchCountEl) {
        searchCountEl.textContent = `${countValue} results found for “${searchTerm}”`;
      }


        (r = [...r, { html: e, url: s }]),
          this.querySelector("filter-sort").dispatchEvent(
            new CustomEvent(this._events.fetch, { detail: { html: e } })
          ),
          this._renderProducts(t);
      });
  }
  _renderElement(e, t) {
    this.querySelector(e).innerHTML = t.querySelector(e).innerHTML;
  }
  _renderProducts(e) {
    this._renderElement(this._selectors.productGrid, e),
      this._renderElement(this._selectors.pagination, e);

    const newCount = e.querySelector("[js-products-count]");
    const oldCount = this.querySelector("[js-products-count]");

    if (newCount && oldCount) { oldCount.innerHTML = newCount.innerHTML; }

    // NEW: update the selected filter chips
    this._renderElement("[js-selected-filters]", e);
  }
}
class FilterSort extends HTMLElement {
  constructor() {
    super(),
      (this.onActiveFilterClick = this.onActiveFilterClick.bind(this)),
      (this._selectors = {
        form: "[js-form]",
        filter: "[js-filter]",
        mobile: "[js-mobile]",
        desktop: "[js-desktop]",
        productsCounter: "[js-products-count]",
        activeFilters: "[js-active-filters]",
        activeFiltersCount: "[js-filter-count]",
        desktopFiltersContainer: "[js-desktop-filters-container]",
        filterInStockCheckbox: "[js-in-stock-input]",
      }),
      (this._events = { change: "filter:change" }),
      (this._onSubmitHandlerEvent = null),
      (this._debouncedOnSubmit = theme.utils.debounce((e) => {
        this._onSubmitHandler(e);
      }, 500));
  }
  connectedCallback() {
    (this._forms = this.querySelectorAll(this._selectors.form)),
      this._setListeners();
  }
  _setListeners() {
    window.addEventListener("popstate", this._onHistoryChange),
      this._forms.forEach((e) =>
        e.addEventListener("input", this._debouncedOnSubmit)
      ),
      this.addEventListener("section:fetched", this._renderComponent);
  }
  _renderComponent = (e) => {
    e = e.detail.html;
    if (e) {
      const t = new DOMParser()
        .parseFromString(e, "text/html")
        .querySelector("filter-sort");
      this._renderFilters(
        t.querySelector(this._selectors.mobile),
        this.querySelector(this._selectors.mobile)
      ),
        this._renderFilters(
          t.querySelector(this._selectors.desktop),
          this.querySelector(this._selectors.desktop)
        ),
        this._renderProductCounts(t),
        this._renderActiveFilters(t);
    }
  };
  _renderFilters(e, t) {
    const s = this._onSubmitHandlerEvent,
      r = [...e.querySelectorAll(this._selectors.filter)];
    r.forEach((e) => {
      t.querySelector(
        `${this._selectors.filter}[data-index="${e.dataset.index}"]`
      ).innerHTML = e.innerHTML;
    });
    e = r.find((e) => {
      var t = s ? s.target.closest(this._selectors.filter) : void 0;
      return !!t && e.dataset.index === t.dataset.index;
    });
    e && this._renderFilterCounts(e, s.target.closest(this._selectors.filter));
  }
  _renderProductCounts(e) {
    this._refreshElements(this._selectors.productsCounter, e);
  }
  _renderActiveFilters(e) {
    this._refreshElements(this._selectors.activeFilters, e);
  }
  _renderFilterCounts(e, t) {
    var s;
    e &&
      t &&
      ((s = t.querySelector(this._selectors.activeFiltersCount)),
        e.querySelector(this._selectors.activeFiltersCount) &&
        s &&
        (t.querySelector(this._selectors.activeFiltersCount).outerHTML =
          e.querySelector(this._selectors.activeFiltersCount).outerHTML));
  }
  _refreshElements(e, t) {
    const s = t.querySelectorAll(e),
      r = this.querySelectorAll(e);
    r.forEach((e, t) => {
      s[t] && (e.innerHTML = s[t].innerHTML);
    });
  }
  _onSubmitHandler = (e) => {
    e.preventDefault();
    var t = (this._onSubmitHandlerEvent = e).target,
      e = new FormData(e.target.closest("form"));
    const s = new URLSearchParams(e);
    s.has("filter.v.availability") ||
      "filter.v.availability" === t.name ||
      s.append("filter.v.availability", "1"),
      this._propagateChange(s.toString());
  };
  _onHistoryChange = (e) => {
    e = e.state ? e.state.searchParams : FilterSort.searchParamsInitial;
    e !== FilterSort.searchParamsPrev &&
      (this._propagateChange(e, !1), (this._onSubmitHandlerEvent = null));
  };
  onActiveFilterClick = (e) => {
    e.preventDefault();
    e =
      -1 == e.currentTarget.href.indexOf("?")
        ? ""
        : e.currentTarget.href.slice(e.currentTarget.href.indexOf("?") + 1);
    this._propagateChange(e);
  };
  _propagateChange(e, t = !0) {
    this.dispatchEvent(
      new CustomEvent(this._events.change, {
        detail: { searchParams: e },
        bubbles: !0,
      })
    ),
      t && this._updateURLHash(e);
  }
  _updateURLHash(e) {
    history.pushState(
      { searchParams: e },
      "",
      "" + window.location.pathname + (e && "?".concat(e))
    );
  }
}
(FilterSort.searchParamsInitial = window.location.search.slice(1)),
  (FilterSort.searchParamsPrev = window.location.search.slice(1));
class PriceRange extends HTMLElement {
  constructor() {
    super(),
      this.querySelectorAll("input").forEach((e) =>
        e.addEventListener("change", this.onRangeChange.bind(this))
      ),
      this.setMinAndMaxValues();
  }
  onRangeChange(e) {
    this.adjustToValidValues(e.currentTarget), this.setMinAndMaxValues();
  }
  setMinAndMaxValues() {
    var e = this.querySelectorAll("input");
    const t = e[0],
      s = e[1];
    s.value && t.setAttribute("max", s.value),
      t.value && s.setAttribute("min", t.value),
      "" === t.value && s.setAttribute("min", 0),
      "" === s.value && t.setAttribute("max", s.getAttribute("max"));
  }
  adjustToValidValues(e) {
    var t = Number(e.value),
      s = Number(e.getAttribute("min")),
      r = Number(e.getAttribute("max"));
    t < s && (e.value = s), r < t && (e.value = r);
  }
}
class FilterRemove extends HTMLElement {
  constructor() {
    super(),
      this.querySelector("a").addEventListener("click", (e) => {
        e.preventDefault(),
          document.querySelector("filter-sort").onActiveFilterClick(e);
      });
  }
}
class MainProduct extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        addToCart: "[js-add-to-cart]",
        price: "[js-price]",
        form: "[js-product-form]",
        loader: "loading-spinner",
        upButton: "[js-prev-button]",
        downButton: "[js-next-button]",
        carousel: "[js-carousel]",
        carouselSlide: "[js-carousel-slide]",
        carouselNav: "[js-carousel-dots]",
        formContainer: "[js-form-container]",
        fixedSelect: "[js-product-fixed-select]",
        swatchInput: "[js-product-swatch-input]",
        mainSelect: "[js-product-original-select]",
        galleryZoom: "[js-open-gallery-zoom]",
        checkBoxContainer: "[js-checkbox-enabled]",
        checkBox: "[js-checkbox]",
        warningCheck: "[js-warning-check]",
        warning: "[js-warning]",
        addToCartCheckbox: "atc-checkbox",
        acknowledgementModal: "acknowledgement-modal",
      }),
      (this.topPercentage = 0),
      (this.carouselImageCount = this.querySelectorAll(
        this._selectors.carouselSlide
      ).length),
      (this.carouselNav = this.querySelector(this._selectors.carouselNav)),
      (this.carouselNavButtons = this.carouselNav.querySelectorAll("button")),
      (this.carouselSlides = this.querySelectorAll(
        this._selectors.carouselSlide
      )),
      (this.galleryZoom = this.querySelector(this._selectors.galleryZoom)),
      (this.totalScrollable = this.carouselImageCount - 4),
      (this.sliderElement = this.querySelector(this._selectors.carousel)),
      (this.carousel = Flickity.data(this.sliderElement));
  }
  connectedCallback() {
    this.addEventListener("variant:change", this._handleVariantChange),
      this._initProductForm(),
      this._initSlides(),
      this._handleReadMore(),
      null !== this.querySelector(".product__reveal-button") &&
      this.querySelector(".product__reveal-button").addEventListener(
        "click",
        this._descriptionLength
      ),
      null ==
      this.querySelector(this._selectors.formContainer + ".matching-set") &&
      this._syncFormSelections(),
      this.galleryZoom.addEventListener("click", this._handleGalleryZoom),
      this.querySelector(this._selectors.checkBoxContainer) &&
      (this._initCheckbox(),
        this._initWarning(),
        this._handleAcknowledgementCheckbox()),
      this.querySelector(this._selectors.carouselSlide + ".hidden") &&
      this._resetSliderIndexes();
  }
  _initWarning() {
    const t = this.querySelector(this._selectors.warning);
    var s = document.querySelector("[js-checkbox]");
    const e = this.querySelector(this._selectors.warningCheck);
    s.addEventListener("change", function (e) {
      e.target.checked
        ? t.classList.add("hidden")
        : t.classList.remove("hidden");
    }),
      e.addEventListener("click", function (e) {
        s.checked || t.classList.remove("hidden");
      });
  }
  _initCheckbox() {
    var e = document.querySelector("[js-checkbox]"),
      t = document.querySelector(".product_atc");
    const s = this.querySelector(this._selectors.warningCheck);
    this.querySelector(this._selectors.warning);
    e.addEventListener("change", function (e) {
      e.target.checked
        ? ((t.disabled = !1),
          s.classList.contains("hidden") || s.classList.add("hidden"))
        : ((t.disabled = !0),
          s.classList.contains("hidden") && s.classList.remove("hidden"));
    });
  }
  _initProductForm() {
    const t = this.querySelector(this._selectors.form),
      s = this.querySelectorAll(this._selectors.addToCart);
    t &&
      t.addEventListener("submit", async (e) => {
        e.preventDefault(),
          s.forEach((e) => {
            e.setAttribute("disabled", ""),
              e
                .querySelector(this._selectors.loader)
                ?.setAttribute("loading", "");
          });
        try {
          await theme.CartAPI.addFromForm(t);
        } catch (e) {
        } finally {
          s.forEach((e) => {
            e.removeAttribute("disabled"),
              e
                .querySelector(this._selectors.loader)
                ?.removeAttribute("loading");
          });
        }
      });
  }
  _initSlides = () => {
    this.querySelector(this._selectors.downButton).addEventListener(
      "click",
      this._slideDown
    ),
      this.querySelector(this._selectors.upButton).addEventListener(
        "click",
        this._slideUp
      );
  };
  _slideDown = () => {
    var e = this.querySelector(this._selectors.downButton),
      t = this.querySelector(this._selectors.upButton),
      s = (this.topPercentage++, -22.22 * this.topPercentage + "%");
    this.topPercentage < this.totalScrollable
      ? ((this.carouselNav.style.top = s),
        e.classList.contains("hidden") && e.classList.remove("hidden"),
        t.classList.remove("hidden"))
      : this.topPercentage == this.totalScrollable &&
      ((this.carouselNav.style.top = s),
        t.classList.remove("hidden"),
        e.classList.add("hidden"),
        document
          .querySelector(".carousel__buttons")
          .classList.add("hide-fade"));
  };
  _slideUp = () => {
    var e = this.querySelector(this._selectors.downButton),
      t = this.querySelector(this._selectors.upButton),
      s = (this.topPercentage--, -22.22 * this.topPercentage + "%");
    document
      .querySelector(".carousel__buttons")
      .classList.contains("hide-fade") &&
      document
        .querySelector(".carousel__buttons")
        .classList.remove("hide-fade"),
      0 == this.topPercentage
        ? ((this.carouselNav.style.top = s), t.classList.add("hidden"))
        : ((this.carouselNav.style.top = s),
          t.classList.contains("hidden") && t.classList.remove("hidden")),
      e.classList.remove("hidden");
  };
  _handleReadMore() {
    var e = this.querySelector(".product__description-wrapper");
    null !== e &&
      e.offsetHeight <= 64 &&
      (this.querySelector(".product__reveal").style.display = "none");
  }
  _descriptionLength() {
    var e = document.querySelector(".product__description");
    "" != e.style.height
      ? ((e.style.height = null),
        (document.querySelector(".product__reveal-button-text").innerHTML =
          "Read Less"))
      : ((e.style.height = "62px"),
        (document.querySelector(".product__reveal-button-text").innerHTML =
          "Read More"));
  }
  _handleVariantChange = (e) => {
    var { variant: e, changedPrice: t } = e.detail;
    this._selectVariantImage(e),
      this._updateAddToCartState(e),
      this._updatePresaleIndications(e),
      e && t && this._updatePrice(e);
  };
  _selectVariantImage = (e) => {
    if (!e || !e.featured_media) return;

    var t;
    e.featured_media &&
      (null == this.carousel &&
        (this.carousel = new Flickity(this._selectors.carousel)),
        e.featured_media.alt
          ? ((t = this._getVariantImagePositionByAlt(
            e.featured_media.alt,
            e.title
          )),
            this.carousel.select(t, !1))
          : this.carousel.select(e.featured_media.position - 1, !1));
  };
  _getVariantImagePositionByAlt = (t, e) => {
    const s = Array.from(this.carouselSlides);
    var r = s.findIndex((e) => e.getAttribute("data-alt") === t);
    const i = s[r];
    return (
      i.classList.contains("hidden") &&
      ((r = i.getAttribute("data-original-index")),
        i.parentNode.classList.contains("flickity-slider") ||
        this.carousel.element
          .querySelector(".flickity-slider")
          .appendChild(i),
        this._manageSlideVisibility(i, this.carouselNavButtons[r], !0),
        this._handleSlideChange(e)),
      i.getAttribute("data-index")
    );
  };
  _manageSlideVisibility(e, t, s) {
    e.classList.toggle("hidden", !s),
      t.classList.toggle("hidden", !s),
      e.classList.toggle("carousel-img", s),
      t.classList.toggle("product-carousel__dot", s);
  }
  _handleSlideChange = (r) => {
    this.carousel.cells.forEach((e) => {
      const t = e.element,
        s = t.dataset.alt;
      s &&
        (e = s.match(/\((.*?)\)/)) &&
        1 < e.length &&
        e[1] !== r &&
        ((e = this.carouselNavButtons[t.getAttribute("data-original-index")]),
          this._manageSlideVisibility(t, e, !1));
    }),
      this._resetSliderIndexes(),
      this.carousel.reloadCells(),
      this._addDotListeners();
  };
  _getVariantTitleFromAlt(e) {
    return e.match(/\((.*?)\)/)[1];
  }
  _resetSliderIndexes() {
    const e = Array.from(this.querySelectorAll(".product-carousel__dot")),
      t =
        (e.forEach((e, t) => {
          (e.dataset.index = t),
            e.setAttribute("data-index", t),
            e.setAttribute("aria-label", "Go to slide " + (t + 1));
        }),
          this.querySelectorAll(".carousel-img"));
    t.forEach((e, t) => {
      e.setAttribute("data-index", t);
    });
  }
  _addDotListeners() {
    var e = new CustomEvent("resetDotListeners");
    this.carouselNav.dispatchEvent(e);
  }
  _updatePresaleIndications(e) {
    const t = document.querySelector("[js-presale-estimated-shipping]");
    if (e && e.available) {
      e = document.querySelector(
        `[js-product-original-select] option[value="${e.id}"]`
      ).dataset.inventoryQuantity;
      const s = document.querySelectorAll("[js-presale-item-property]");
      0 < e
        ? (s.forEach((e) => {
          e.setAttribute("disabled", "");
        }),
          t && t.classList.add("hidden"))
        : (s.forEach((e) => {
          e.removeAttribute("disabled");
        }),
          t && t.classList.remove("hidden"));
    } else t && t.classList.add("hidden");
  }
  _updateAddToCartState(t) {
    const e = this.querySelectorAll(this._selectors.addToCart);
    let s = !1;
    const r = this.querySelector(this._selectors.warningCheck),
      i = this.querySelector(this._selectors.warning);
    let o = document.querySelector("[js-checkbox]");
    if ((document.querySelector("[js-checkbox-enabled]") && (s = !0), t)) {
      var n = document.querySelector(
        `[js-product-original-select] option[value="${t.id}"]`
      ).dataset.inventoryQuantity;
      let qtycsValue = document.getElementById('qtycs').value;
      // Check if n has no value (undefined, null, empty, etc.)
      if (!n) {
        // If n is not set, check qtycsValue
        if (qtycsValue) {
          n = 3;
        }
      }
      t.available
        ? e.forEach((e) => {
          e.classList.remove("sold-out"),
          s
            ? ((document.querySelector(
              "[js-checkbox-enabled]"
            ).style.display = "block"),
              i.classList.contains("hidden") || i.classList.add("hidden"),
              o && o.checked
                ? ((e.disabled = !1),
                  r.classList.contains("hidden") || r.classList.add("hidden"))
                : (e.setAttribute("disabled", ""),
                  r.classList.contains("hidden") &&
                  r.classList.remove("hidden")))
            : e.removeAttribute("disabled"),
            0 < n
              ? (e.textContent = "Add To Cart")
              : "true" == this.dataset.presale || t.available
                ? (e.textContent = "Pre-Order")
                : ((e.textContent = "Sold out"),
                  e.setAttribute("disabled", ""),
                  s &&
                  (o && (o.checked = !1),
                    (document.querySelector(
                      "[js-checkbox-enabled]"
                    ).style.display = "none"),
                    r.classList.contains("hidden") ||
                    r.classList.add("hidden")));
        })
        : e.forEach((e) => {
          (e.textContent = "Sold out"),
            e.setAttribute("disabled", ""),
            e.classList.add("sold-out"),
            s &&
            (o && (o.checked = !1),
              (document.querySelector("[js-checkbox-enabled]").style.display =
                "none"),
              r.classList.contains("hidden") || r.classList.add("hidden"));
        });
    } else
      e.forEach((e) => {
        (e.textContent = "Unavailable"),
          e.setAttribute("disabled", ""),
          e.classList.add("sold-out"),
          s &&
          (o && (o.checked = !1),
            (document.querySelector("[js-checkbox-enabled]").style.display =
              "none"),
            r.classList.contains("hidden") || r.classList.add("hidden"));
      });
  }
  _updatePrice(t) {
    const e = this.querySelectorAll(this._selectors.price);
    e.forEach((e) =>
      e.textContent(theme.utils.formatMoney(t.price, theme.config.money.format))
    );
  }
  _evaluatePosition() {
    var e = document.querySelector(".product__form-wrapper"),
      t = e.offsetHeight,
      t =
        ((e.style.height = t + "px"), document.querySelector(".product__form")),
      s = document.querySelector(".form"),
      r = document.querySelector(".btn-container"),
      i = document.querySelector(".no-js-hidden"),
      o = document.querySelector(".product-header__nav"),
      n = document.querySelector(".product-swatch__header-sizeguide"),
      a = document.querySelector(".product-swatch-colors__container "),
      c = document.querySelector(".product-swatch__container"),
      l = document.querySelectorAll(".product-swatch__color-label"),
      d = document.querySelector(".accessibility-position-middle-left");
    e.getBoundingClientRect().top < -200
      ? (t.classList.add("fixed-form", "border-t", "border-black"),
        i.classList.add("form-flexed"),
        s.classList.add("form-flex"),
        r.classList.remove("w-full", "btn-flex"),
        o.classList.add("header-flex"),
        n && n.classList.add("hidden"),
        a && (a.classList.remove("pt-28"), a.classList.add("swatch-flex")),
        c.classList.remove("pt-28"),
        l.length &&
        l.forEach((e) => {
          e.classList.remove("p-20");
        }),
        null !== d && d.classList.add("!top-1/3"))
      : ((e.style.height = "auto"),
        t.classList.remove("fixed-form", "border-t", "border-black"),
        i.classList.remove("form-flexed"),
        s.classList.remove("form-flex"),
        r.classList.add("w-full", "btn-flex"),
        o.classList.remove("header-flex"),
        o.classList.add("hidden"),
        n && n.classList.remove("hidden"),
        a && a.classList.remove("swatch-flex"),
        l.length &&
        l.forEach((e) => {
          e.classList.add("p-20");
        }),
        null !== d &&
        d.length &&
        d.classList.contains("!top-1/3") &&
        d.classList.remove("!top-1/3"));
  }
  _syncFormSelections() {
    document
      .querySelector(this._selectors.fixedSelect)
      .addEventListener("change", this._updateHiddenInputs),
      document
        .querySelector(".fixed-product-form__atc")
        .addEventListener("click", this._addVariantFromFixedForm.bind(this));
    const t = this;
    document.addEventListener("variant:change", function (e) {
      t._handleVariantChange(e), t._updateFixedFormElements(e.detail);
    });
  }
  _updateHiddenInputs() {
    // Scope to main product section to avoid carousel interference
    const mainProductSection = document.querySelector('[id^="shopify-section-main-product"], .main-product, main-product');
    if (!mainProductSection) return;

    let e = mainProductSection.querySelector(
      `.product-swatch__label[title="${this.value}"]`
    );
    if (e) e.click();
  }
  _addVariantFromFixedForm() {
    return;
    var e = document.querySelector(this._selectors.mainSelect),
      e = parseInt(e.value);
    theme.CartAPI.add({ items: [{ id: e, quantity: 1 }] });
  }
  _updateFixedFormElements(t) {
    if (!t || !t.variant) return;

    var s = t.variant;
    t.priceChange;
    let e = s.options;
    if (void 0 !== s) {
      t = s.featured_image;
      let e = this.querySelector(".product-header__nav-img");
      e.innerHTML = `<img src="${t.src}" alt="${t.alt}" />`;
    }
    1 < e.length
      ? e.forEach((e) => {
        let t = document.querySelector(
          `.fixed-product-form .color-swatch[value="${e}"]`
        );
        null !== t
          ? (t.checked = !0)
          : (this.querySelector(this._selectors.fixedSelect).value = e);
      })
      : (this.querySelector(this._selectors.fixedSelect).value = s.title);
  }
  _handleGalleryZoom() {
    var e = document.querySelector(".is-selected img");
    const t = document.getElementById("GalleryZoomModal").querySelector("img");
    e && t && (t.srcset = e.srcset);
  }
  _handleAcknowledgementCheckbox() {
    const t = document.getElementById(this._selectors.addToCartCheckbox),
      e = document.querySelector(this._selectors.warningCheck),
      s = document.getElementById(this._selectors.acknowledgementModal);
    t.addEventListener("change", function (e) {
      e.target.checked && s.classList.add("hidden");
    }),
      e.addEventListener("click", function (e) {
        0 == t.checked && s.classList.remove("hidden");
      });
  }
}
function calcHeaderHeight() {
  var e = document.querySelector("#shopify-section-header");
  const t = document.querySelector("#MobileNavDrawer"),
    s = document.querySelector("#SearchDrawer");
  e = e.offsetHeight;
  (t.style.top = e + "px"),
    (s.style.height = e + "px"),
    document.documentElement.style.setProperty("--header-height", e + "px");
}

// (() => {
//   const $header    = document.querySelector('#shopify-section-header');
//   const $mobileNav = document.querySelector('#MobileNavDrawer');
//   const $search    = document.querySelector('#SearchDrawer');
//   if (!$header || (!$mobileNav && !$search)) return;

//   const NUDGE = 1; // px to hide the thin border line

//   let ticking = false;
//   const req = () => { if (!ticking) (ticking = true, requestAnimationFrame(update)); };

//   function update() {
//     ticking = false;
//     const rect = $header.getBoundingClientRect();
//     const headerHeight = Math.round(rect.height);
//     const headerBottom = Math.max(0, Math.round(rect.bottom));

//     // expose both values (handy in CSS)
//     document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
//     document.documentElement.style.setProperty('--header-bottom', `${headerBottom}px`);

//     // ↓ subtract 1px to avoid showing the input's bottom border
//     if ($mobileNav) $mobileNav.style.top = `${Math.max(0, headerBottom - NUDGE)}px`;
//     if ($search)    $search.style.height = `${Math.max(0, headerHeight - NUDGE)}px`;
//   }

//   req();
//   window.addEventListener('scroll', req, { passive: true });
//   window.addEventListener('resize', req);
//   if ('ResizeObserver' in window) new ResizeObserver(req).observe($header);
//   $header.addEventListener('transitionend', req, { passive: true });
// })();

(() => {
  const $header = document.querySelector('#shopify-section-header');
  const $mobileNav = document.querySelector('#MobileNavDrawer');
  const $search = document.querySelector('#SearchDrawer');
  const $filter = document.querySelector('#FilterDrawer'); // ← add
  if (!$header || (!$mobileNav && !$search && !$filter)) return;

  const NUDGE = 1; // px to hide the thin border line

  let ticking = false;
  const req = () => { if (!ticking) (ticking = true, requestAnimationFrame(update)); };

  function update() {
    ticking = false;
    const rect = $header.getBoundingClientRect();
    const headerHeight = Math.round(rect.height);
    const headerBottom = Math.max(0, Math.round(rect.bottom));
    const top = Math.max(0, headerBottom - NUDGE);

    // expose both values
    document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
    document.documentElement.style.setProperty('--header-bottom', `${headerBottom}px`);

    if ($mobileNav) $mobileNav.style.top = `${top}px`;
    if ($search) $search.style.height = `${Math.max(0, headerHeight - NUDGE)}px`;

    // ← offset the filter drawer too
    if ($filter) {
      $filter.style.top = `${top}px`;
      $filter.style.height = `calc(100vh - ${top}px)`;
      $filter.style.maxHeight = `calc(100svh - ${top}px)`; // better on mobile address bars
    }
  }

  req();
  window.addEventListener('scroll', req, { passive: true });
  window.addEventListener('resize', req);
  if ('ResizeObserver' in window) new ResizeObserver(req).observe($header);
  $header.addEventListener('transitionend', req, { passive: true });
})();


function initAnnouncementSlider() {
  const e = document.querySelector(".announcement-bar.slider .main-carousel"),
    t = new Flickity(e, {
      cellAlign: "left",
      wrapAround: !0,
      autoPlay: 4e3,
      pageDots: !1,
      prevNextButtons: !1,
      contain: !1,
      adaptiveHeight: !0,
      cellSelector: ".carousel-cell",
    }),
    s =
      ((e.parentNode.style.display = "block"),
        t.resize(),
        document.querySelector(".announcement-bar.slider")),
    r = s.querySelector(".announcement-nav--next"),
    i = s.querySelector(".announcement-nav--prev");
  r.addEventListener("click", () => t.next("next", !1)),
    i.addEventListener("click", () => t.previous("previous", !1)),
    handleArrowColors(t, { next: r, prev: i });
}
function handleArrowColors(s, t) {
  const r = (e) => {
    (t.next.querySelector("svg path").style.fill = e),
      (t.prev.querySelector("svg path").style.fill = e);
  },
    i = ".announcement__text",
    e = s.selectedCells[0].element;
  var o = e.querySelector(i).style.color;
  r(o),
    s.on("change", function (e) {
      const t = s.cells[e].element;
      e = t.querySelector(i).style.color;
      r(e);
    });
}
function handleResponsiveArrows(s, t) {
  const r = (e) => {
    (t.prev.style.left = `calc(50% - ${e}px)`),
      (t.next.style.right = `calc(50% - ${e}px)`);
  },
    e = s.selectedCells[0].element;
  var i = e.querySelector(".announcement__text").offsetWidth;
  r(i),
    (t.prev.style.opacity = "0.75"),
    (t.next.style.opacity = "0.75"),
    s.on("change", function (e) {
      const t = s.cells[e];
      var e = t.element.querySelector(".announcement__text").offsetWidth;
      0 < e && ((e = 240 < e ? 200 : e), r(e));
    });
}
function handleAnnoucements() {
  var t = window.sessionStorage;
  const e = document.querySelectorAll("[js-collapsable-close]");
  document.querySelectorAll(".site-header__menu-item__section");
  e.forEach((e) => {
    (null !== t.getItem("hide-" + e.parentNode.id)
      ? hideAnnoucements
      : showAnnoucements)(e),
      e.addEventListener("click", () => {
        t.setItem("hide-" + e.parentNode.id, !0),
          hideAnnoucements(e),
          calcHeaderHeight();
      });
  });
  const s = document.querySelector("section.announcement-bar");
   s && s.classList.contains("slider") && initAnnouncementSlider(),
    calcHeaderHeight();
}
function hideAnnoucements(e) {
  const t = e.closest(".announcement-bar");
  t && t.classList.add("hidden");
}
function showAnnoucements(e) {
  const t = e.closest(".announcement-bar");
  t && t.classList.remove("hidden");
}
document.addEventListener("MobileNavDrawer:open", () => {
  document.getElementById("header-open-nav").classList.add("hidden"),
    document.getElementById("header-close-nav").classList.remove("hidden");
}),
  document.addEventListener("MobileNavDrawer:close", () => {
    document.getElementById("header-close-nav").classList.add("hidden"),
      document.getElementById("header-open-nav").classList.remove("hidden");
  }),
  handleAnnoucements();
class ProductRecommendations extends HTMLElement {
  constructor() {
    super();

    // Set up IntersectionObserver
    new IntersectionObserver(
      (entries, observer) => {
        if (entries[0].isIntersecting) {
          observer.unobserve(this);
          fetch(this.dataset.url)
            .then((response) => response.text())
            .then((htmlContent) => {
              const container = document.createElement("div");
              container.innerHTML = htmlContent;
              const recommendations = container.querySelector("product-recommendations");
              if (recommendations && recommendations.innerHTML.trim().length) {
                this.innerHTML = recommendations.innerHTML;
                $(".product-card__quickadd-mobile-btn").on("click", function (event) {
                  event.preventDefault(); // Prevents the anchor link from opening
                  event.stopPropagation(); // Stops propagation to outer elements
                });
                document.dispatchEvent(
                  new CustomEvent("product-recommendations:loaded", { detail: { element: this } })
                );
              }
            })
            .catch((error) => {
              console.error("Error fetching product recommendations:", error);
            });
        }
      },
      { rootMargin: "0px 0px 200px 0px" }
    ).observe(this);
  }
}
customElements.define("main-collection", MainCollection),
  customElements.define("filter-sort", FilterSort),
  customElements.define("price-range", PriceRange),
  customElements.define("filter-remove", FilterRemove),
  customElements.define("main-product", MainProduct),
  customElements.define("product-recommendations", ProductRecommendations);
class CustomerAddresses extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        customerAddresses: "[js-customer-addresses]",
        addressCountrySelect: "[js-country-select]",
        editAddressModal: "[js-edit-address-modal]",
        editAddressButton: "[js-edit-address]",
        editAddressForm: "[js-address-edit-form]",
        addressContainer: "[data-address]",
        cancelAddressButton: 'button[type="reset"]',
        deleteAddressButton: "[js-delete-address]",
      }),
      (this.elements = this._getElements()),
      this._setupCountries(),
      this._setupEventListeners();
  }
  _getElements() {
    return {
      deleteButtons: this.querySelectorAll(this._selectors.deleteAddressButton),
      countrySelects: this.querySelectorAll(
        this._selectors.addressCountrySelect
      ),
      editAddressButtons: this.querySelectorAll(
        this._selectors.editAddressButton
      ),
      editAddressModal: this.querySelector(this._selectors.editAddressModal),
      editAddressForms: this.querySelectorAll(this._selectors.editAddressForm),
    };
  }
  _setupCountries() {
    Shopify &&
      Shopify.CountryProvinceSelector &&
      (new Shopify.CountryProvinceSelector(
        "AddressCountryNew",
        "AddressProvinceNew",
        { hideElement: "AddressProvinceContainerNew" }
      ),
        this.elements.countrySelects.forEach((e) => {
          e = e.dataset.formId;
          new Shopify.CountryProvinceSelector(
            "AddressCountry_" + e,
            "AddressProvince_" + e,
            { hideElement: "AddressProvinceContainer_" + e }
          );
        }));
  }
  _setupEventListeners() {
    this.elements.editAddressButtons.forEach((e) => {
      e.addEventListener("click", this._handleAddEditButtonClick);
    }),
      this.elements.deleteButtons.forEach((e) => {
        e.addEventListener("click", this._handleDeleteButtonClick);
      }),
      this.elements.editAddressModal &&
      this.elements.editAddressModal.addEventListener(
        "close",
        this._handleModalClose
      );
  }
  _handleAddEditButtonClick = (e) => {
    e.stopPropagation();
    e = e.currentTarget.dataset.form;
    const t = document.querySelector("#address_form_" + e);
    t.classList.remove("hidden"),
      document.querySelector("#editAddressModal").open(),
      t.querySelector('[name="address[first_name]"]').focus();
  };
  _handleDeleteButtonClick = ({ currentTarget: e }) => {
    confirm("Are you sure you want to delete this address?") &&
      Shopify.postLink(e.dataset.target, { parameters: { _method: "delete" } });
  };
  _handleModalClose = () => {
    this.elements.editAddressForms.forEach((e) => {
      e.classList.add("hidden");
    });
  };
}
class CustomerAccount extends HTMLElement {
  constructor() {
    super(),
      (this._selectors = {
        accountSelect: "[js-account-select]",
        accountAnchor: "[js-account-anchor]",
        accountSelectOption: "[js-account-select-option]",
        accountLink: "[js-account-link]",
      }),
      (this.elements = this._getElements()),
      this._setupEventListeners();
  }
  _getElements() {
    return {
      accountMenuSelect: this.querySelector(this._selectors.accountSelect),
      accountAnchoredSections: this.querySelectorAll(
        this._selectors.accountAnchor
      ),
      accountSelectOptions: this.querySelectorAll(
        this._selectors.accountSelectOption
      ),
      accountLinks: this.querySelectorAll(this._selectors.accountLink),
    };
  }
  _setupEventListeners() {
    sessionStorage.getItem("lastOption") || sessionStorage.getItem("lastLink")
      ? this._getSessionStorage()
      : window.addEventListener("load", this._findCurrentLink),
      this.elements.accountMenuSelect &&
      this.elements.accountMenuSelect.addEventListener(
        "change",
        this._navigateAccountSeclect
      ),
      this.elements.accountLinks.forEach((e) => {
        e.addEventListener("click", this._styleClickedLink);
      });
  }
  _getSessionStorage = () => {
    (this.elements.accountMenuSelect.value =
      sessionStorage.getItem("lastOption")),
      sessionStorage.removeItem("lastOption");
    var e = sessionStorage.getItem("lastLink");
    document.getElementById(e) &&
      (document.getElementById(e).classList.add("account-link-active"),
        sessionStorage.removeItem("lastLink"));
  };
  _findCurrentLink = () => {
    this.elements.accountAnchoredSections.forEach((t) => {
      "block" === window.getComputedStyle(t).display &&
        (this.elements.accountSelectOptions.forEach((e) => {
          e.id == "option-" + t.id && (e.selected = !0);
        }),
          this.elements.accountLinks.forEach((e) => {
            e.id == "link-" + t.id && e.classList.add("account-link-active");
          }));
    });
  };
  _navigateAccountSeclect = () => {
    sessionStorage.setItem("lastOption", this.elements.accountMenuSelect.value),
      (window.location = this.elements.accountMenuSelect.value);
  };
  _styleClickedLink = (t) => {
    t.target.classList.add("account-link-active"),
      this.elements.accountLinks.forEach((e) => {
        e != t.target && e.classList.remove("account-link-active");
      }),
      sessionStorage.setItem("lastLink", t.target.id);
  };
}
customElements.define("customer-addresses", CustomerAddresses),
  customElements.define("customer-account", CustomerAccount);

// document.addEventListener('DOMContentLoaded', () => {
//   const seenProducts = new Set();
//   document.querySelectorAll('[data-product-id]').forEach(card => {
//     const productId = card.getAttribute('data-product-id');
//     if (seenProducts.has(productId)) {
//       card.remove(); // or card.style.display = 'none';
//     } else {
//       seenProducts.add(productId);
//     }
//   });
// });
